import React, { useEffect, useState, useMemo } from 'react';
import './client-css/client-dashboard-global.css';
import './client-css/client-dashboard-dark.css';
import './client-css/client-dashboard-light.css';
import { cleanDisplayValue } from '../../utils/displayUtils';
import { getFieldValue, hasValidFieldValue, COMMON_FIELD_COMBINATIONS } from '../../utils/robustColumnMatching';
import AnimatedCounter from '../../components/AnimatedCounter';
import MarqueeChip from '../../components/MarqueeChip';
import {
  Box, 
  Container, 
  Typography, 
  Alert, 
  CircularProgress, 
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Checkbox,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Menu,
  ListItemText,
  FormControlLabel,
  Collapse,
} from '@mui/material';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  BarChart as BarChartIcon,

  Search as SearchIcon,
  List as ListIcon,
  EmojiEvents as TrophyIcon,
  PieChart as PieChartIcon,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  Add as AddIcon,
  Remove as RemoveIcon,
  ThumbUp as EngagementIcon,
  Visibility as InteractionIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import api from '../../utils/api';

// Custom SVG Icon Components - Updated with Material Symbols
// Total Leads Icon - Contacts/People
const CustomContactsIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01.99L14.5 10h-1.75l1.51-1.99C14.3 7.37 13.56 7 12.75 7H11c-.8 0-1.54.37-2.01.99L7.75 10H6l1.51-1.99C7.8 7.37 7.06 7 6.25 7H4.46c-.8 0-1.46.63-1.42 1.37L5.5 18H8v4h12z" fill="#8280FF"/>
  </svg>
);

// Interested Leads Icon - Favorite/Heart
const CustomFavoriteIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#FEC53D"/>
  </svg>
);

// Brand Engagement Icon - Analytics/Chart
const CustomAnalyticsIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="#FEC53D"/>
  </svg>
);

// Competitor Brand Engagement Icon - Competition/Rival
const CustomCompetitionIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="rgba(255, 144, 102, 0.9013)"/>
  </svg>
);

// Second Row Custom Icons - Material Symbols Style
// Top Industries Icon - Factory/Industry
const CustomIndustryIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" fill="#8280FF"/>
  </svg>
);

// Top Job Titles Icon - Badge/Person
const CustomBadgeIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1L13.5 2.5L16.17 5.17L12 9.34L7.83 5.17L10.5 2.5L9 1L3 7V9H21ZM3 19V21H21V19H3ZM12 10C13.1 10 14 10.9 14 12C14 13.1 13.1 14 12 14C10.9 14 10 13.1 10 12C10 10.9 10.9 10 12 10Z" fill="#FEC53D"/>
  </svg>
);

// Top Intent Keyword 7 Days Icon - Trending Up
const CustomTrendingUpIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" fill="#4AD991"/>
  </svg>
);

// Top Intent Keyword 30 Days Icon - Trophy
const CustomTrophyIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 4V2C7 1.45 7.45 1 8 1H16C16.55 1 17 1.45 17 2V4H20C20.55 4 21 4.45 21 5S20.55 6 20 6H19V7C19 9.76 16.76 12 14 12H13V16H16C16.55 16 17 16.45 17 17S16.55 18 16 18H8C7.45 18 7 17.55 7 17S7.45 16 8 16H11V12H10C7.24 12 5 9.76 5 7V6H4C3.45 6 3 5.55 3 5S3.45 4 4 4H7ZM9 3V4H15V3H9ZM7 6V7C7 8.66 8.34 10 10 10H14C15.66 10 17 8.66 17 7V6H7Z" fill="#FF9066"/>
  </svg>
);

// Top Intent Keyword 90 Days Icon - Star
const CustomStarIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="#9C27B0"/>
  </svg>
);

// Website Visitors Icon - Web/Globe
const CustomWebIcon: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" fill="rgba(99, 102, 241, 0.8)"/>
  </svg>
);

interface DashboardData {
  clientName: string;
  companyName: string;
  isTemporaryAccess: boolean;
  username: string;
}

interface SheetData {
  _id: string;
  fileName: string;
  originalFileName: string;
  recordCount: number;
  version: number;
  columns: string[];
  data: any[];
  uploadDate: string;
  fileType: string;
}

interface UploadedData {
  hasData: boolean;
  sheets: SheetData[];
  message?: string;
}

interface BuyingIntentItem {
  topic: string;
  count: number;
}

interface LegacyBuyingIntentItem {
  topic: string;
  count: number;
}

interface BuyingIntentData {
  hasData: boolean;
  buyingIntents: Array<{
    keyword: string;
    category: string;
    uploadedAt: string;
    label?: 'client' | 'competitor' | 'unclassified';
  }>;
  message?: string;
}

interface InsuranceAnalytics {
  totalLeads: number;
  totalInterestedLeads: number;
  totalGenericBuyingIntents: number;
  totalBrandedBuyingIntents: number;
  timesClientBrandSearched: number;
  competitorBrands: number;
  websiteVisitors: number;
  topIndustries: string[];
  topJobPositions: string[];
  intent7DaysCount: number;
  intent30DaysCount: number;
  intent90DaysCount: number;
  topIntent7Days: string;
  topIntent30Days: string;
  topIntent90Days: string;
  totalBuyingIntent7Days: number;
  totalBuyingIntent30Days: number;
  totalBuyingIntent90Days: number;
  overallAverageBuyingIntent: number;
  primaryInterests: string[];
}

interface CompanyData {
  name: string;
  country: string;
  allKeywords: Set<string>;
  keywords: Set<string>;
  industries: Set<string>;
  totalLeads: number;
  allData: any[];
  latestUploadDate?: Date; // Track when this company's data was last updated
}

interface CountryFilterState {
  searchQuery: string;
  selectedBuyingIntents: string[];
  selectedIndustries: string[];
  selectedBranding: string[];
  timeRange: 'last7days' | 'last30days' | 'last90days';
}

// Custom Bar Chart Component for Buying Intent
interface ChartData {
  keyword: string;
  count: number;
  percentage: number;
}

interface TooltipData {
  keyword: string;
  count: number;
  percentage: number;
  x: number;
  y: number;
  visible: boolean;
}

const EnhancedBuyingIntentBarChart: React.FC<{ 
  countryName: string, 
  companies: CompanyData[], 
  maxBars?: number 
}> = ({ countryName, companies, maxBars = 8 }) => {
  const theme = useTheme();
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc' | 'alphabetical'>('desc');
  const [tooltip, setTooltip] = useState<TooltipData>({
    keyword: '', count: 0, percentage: 0, x: 0, y: 0, visible: false
  });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  // Aggregate buying intent data for this country
  const baseChartData = useMemo((): ChartData[] => {
    const keywordFrequency = new Map<string, number>();
    let totalKeywords = 0;
    
    companies.forEach(company => {
      Array.from(company.keywords as Set<string>).forEach(keyword => {
        keywordFrequency.set(keyword, (keywordFrequency.get(keyword) || 0) + 1);
        totalKeywords++;
      });
    });
    
    if (totalKeywords === 0) return [];
    
    // Convert to chart data
    const data = Array.from(keywordFrequency.entries())
      .map(([keyword, count]) => ({
        keyword,
        count,
        percentage: (count / totalKeywords) * 100
      }))
      .slice(0, maxBars);
    
    return data;
  }, [companies, maxBars]);

  // Sort data based on current sort order
  const chartData = useMemo(() => {
    const sorted = [...baseChartData];
    switch (sortOrder) {
      case 'desc':
        return sorted.sort((a, b) => b.count - a.count);
      case 'asc':
        return sorted.sort((a, b) => a.count - b.count);
      case 'alphabetical':
        return sorted.sort((a, b) => a.keyword.localeCompare(b.keyword));
      default:
        return sorted;
    }
  }, [baseChartData, sortOrder]);
  
  const maxCount = Math.max(...chartData.map(item => item.count), 1);
  
  // Enhanced color generation with darker, more professional colors
  const getBarColor = (index: number, isHovered: boolean, isHighlighted: boolean) => {
    const colors = [
      { primary: '#2E7D32', secondary: '#4CAF50', glow: 'rgba(46, 125, 50, 0.4)' }, // Dark Green
      { primary: '#1565C0', secondary: '#2196F3', glow: 'rgba(21, 101, 192, 0.4)' }, // Dark Blue
      { primary: '#6A1B9A', secondary: '#9C27B0', glow: 'rgba(106, 27, 154, 0.4)' }, // Dark Purple
      { primary: '#D84315', secondary: '#FF5722', glow: 'rgba(216, 67, 21, 0.4)' }, // Dark Orange
      { primary: '#424242', secondary: '#757575', glow: 'rgba(66, 66, 66, 0.4)' }, // Dark Gray
      { primary: '#2E7D32', secondary: '#388E3C', glow: 'rgba(46, 125, 50, 0.4)' }, // Forest Green
      { primary: '#1976D2', secondary: '#42A5F5', glow: 'rgba(25, 118, 210, 0.4)' }, // Steel Blue
      { primary: '#7B1FA2', secondary: '#AB47BC', glow: 'rgba(123, 31, 162, 0.4)' }  // Deep Purple
    ];
    
    const colorSet = colors[index % colors.length];
    
    if (isHovered) {
      return `linear-gradient(135deg, ${colorSet.primary} 0%, ${colorSet.secondary} 100%)`;
    }
    if (isHighlighted) {
      return `linear-gradient(135deg, ${colorSet.primary}DD 0%, ${colorSet.secondary}BB 100%)`;
    }
    return `linear-gradient(135deg, ${colorSet.primary}AA 0%, ${colorSet.secondary}88 100%)`;
  };

  const handleMouseEnter = (item: ChartData, index: number, event: React.MouseEvent) => {
    setHoveredIndex(index);
    setTooltip({
      keyword: item.keyword,
      count: item.count,
      percentage: item.percentage,
      x: event.clientX,
      y: event.clientY,
      visible: true
    });
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: event.clientX,
        y: event.clientY
      }));
    }
  };

  const cycleSortOrder = () => {
    setSortOrder(current => {
      switch (current) {
        case 'desc': return 'asc';
        case 'asc': return 'alphabetical';
        case 'alphabetical': return 'desc';
        default: return 'desc';
      }
    });
  };

  const getSortIcon = () => {
    switch (sortOrder) {
      case 'desc': return '?';
      case 'asc': return '?';
      case 'alphabetical': return 'A-Z';
      default: return '?';
    }
  };
  
  if (chartData.length === 0) {
    return (
      <Card 
        className="intent-keywords-container"
        sx={{ 
          px: 2,
          pt: 1.5,
          pb: 0,
          mt: 2, 
          backgroundColor: '#6219823d',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2
        }}>
        <Typography variant="subtitle1" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: theme.palette.text.secondary,
          fontSize: '0.9rem',
          mb: 1
        }}>
          <BarChartIcon className="dashboard-icon-18" />
          Intent Keywords - {countryName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 1, pb: 0 }}>
          No buying intent data available
        </Typography>
      </Card>
    );
  }
  
  return (
    <>
      <Card 
        className="intent-keywords-container"
        sx={{ 
          px: 2,
          pt: 1.5,
          pb: 0,
          mt: 2, 
          backgroundColor: '#6219823d',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          boxShadow: theme.shadows[2],
          position: 'relative',
          overflow: 'visible'
        }}
      onMouseMove={handleMouseMove}
      >
        <Box className="dashboard-flex-between dashboard-mb-1">
          <Typography variant="subtitle1" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            fontSize: '0.9rem'
          }}>
            <BarChartIcon className="dashboard-icon-18" />
            Intent Keywords - {countryName}
          </Typography>
          
          <Box className="dashboard-flex-between dashboard-gap-1">
            <Chip 
              label={`${chartData.length} keywords`} 
              size="small" 
              color="primary" 
              variant="outlined"
              className="dashboard-chip-small"
            />
            <Button
              variant="outlined"
              size="small"
              onClick={cycleSortOrder}
              className={`${theme.palette.mode === 'dark' ? 'sort-button-dark' : 'sort-button-light'}`}
            >
              {getSortIcon()}
            </Button>
          </Box>
        </Box>
        
        <Box className="dashboard-flex-column dashboard-gap-1 dashboard-chart-height-200 dashboard-overflow-hidden">
          {chartData.map((item, index) => {
            const isHighlighted = index < 3;
            const isHovered = hoveredIndex === index;
            const barWidth = (item.count / maxCount) * 100;
            
            return (
              <Box 
                key={`${item.keyword}-${sortOrder}`} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  opacity: 0,
                  animation: `slideInLeft 0.5s ease-out ${index * 0.1}s forwards`,
                  '@keyframes slideInLeft': {
                    from: { opacity: 0, transform: 'translateX(-20px)' },
                    to: { opacity: 1, transform: 'translateX(0)' }
                  }
                }}
                onMouseEnter={(e) => handleMouseEnter(item, index, e)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Keyword Label */}
                <Box className="dashboard-min-width-180 dashboard-max-width-180">
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: isHighlighted ? 'bold' : 'normal',
                      color: isHighlighted ? theme.palette.primary.main : theme.palette.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '0.7rem',
                      transition: 'all 0.3s ease'
                    }}
                    title={item.keyword}
                  >
                    {item.keyword}
                  </Typography>
                </Box>
                
                {/* Bar */}
                <Box className="dashboard-relative" sx={{ flex: 1, height: 20 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${Math.min(barWidth, 85)}%`, // Limit bar width to prevent overlap
                      background: getBarColor(index, isHovered, isHighlighted),
                      borderRadius: 2,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: isHighlighted ? `1px solid ${theme.palette.primary.main}` : 'none',
                      boxShadow: isHovered ? `0 0 20px ${getBarColor(index, false, false)}` : 
                                isHighlighted ? theme.shadows[3] : theme.shadows[1],
                      transform: isHovered ? 'scaleY(1.2)' : 'scaleY(1)',
                      cursor: 'pointer',
                      '&::after': isHovered ? {
                        content: '""',
                        position: 'absolute',
                        top: -2,
                        left: -2,
                        right: -2,
                        bottom: -2,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}44, transparent)`,
                        borderRadius: 3,
                        zIndex: -1
                      } : {}
                    }}
                  />
                </Box>
                
                {/* Count Column - Fixed Width */}
                <Box sx={{ 
                  minWidth: 45, 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  justifyContent: 'flex-end'
                }}>
                  <Typography
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                      minWidth: '25px',
                      textAlign: 'right',
                    }}
                  >
                    {item.count}
                  </Typography>
                  
                  {/* Highlight Badge - Right next to number */}
                  {isHighlighted && (
                    <TrophyIcon sx={{ 
                      color: theme.palette.primary.main, 
                      fontSize: 12,
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' },
                        '100%': { transform: 'scale(1)' }
                      }
                    }} />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        
        {/* Chart Summary */}
        <Box sx={{ 
          mt: 0, 
          pt: 0, 
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="caption" color="text.secondary" className="dashboard-text-small" sx={{ m: 0 }}>
            {companies.length} companies � {chartData.reduce((sum, item) => sum + item.count, 0)} keywords
          </Typography>
          <Box className="dashboard-flex-between" sx={{ gap: 0.5 }}>
            <Box className="dashboard-width-8 dashboard-height-8" sx={{ 
              background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)', 
              borderRadius: '50%',
              animation: 'glow 2s ease-in-out infinite alternate',
              '@keyframes glow': {
                from: { boxShadow: '0 0 5px rgba(255, 107, 107, 0.5)' },
                to: { boxShadow: '0 0 10px rgba(255, 107, 107, 0.8)' }
              }
            }} />
            <Typography variant="caption" color="text.secondary" className="dashboard-text-small" sx={{ m: 0 }}>
              Top performers
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Interactive Tooltip */}
      {tooltip.visible && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltip.x + 10,
            top: tooltip.y - 60,
            zIndex: 9999,
            background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 1.5,
            boxShadow: theme.shadows[8],
            pointerEvents: 'none',
            animation: 'tooltipFadeIn 0.2s ease-out',
            '@keyframes tooltipFadeIn': {
              from: { opacity: 0, transform: 'scale(0.8)' },
              to: { opacity: 1, transform: 'scale(1)' }
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.8rem' }}>
            {tooltip.keyword}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
            Count: {tooltip.count}
          </Typography>

        </Box>
      )}
    </>
  );
};

// Beautiful Donut Chart Component
interface DonutChartData {
  label: string;
  value: number;
  percentage: number;
  color: string;
}

const BeautifulDonutChart: React.FC<{ 
  data: any[], 
  groupBy: 'country' | 'industry' | 'intent',
  title: string,
  timeRange?: 'last7days' | 'last30days' | 'last90days'
}> = ({ data, groupBy, title, timeRange = 'last7days' }) => {
  const theme = useTheme();
  
  // Process data for donut chart
  const donutData = useMemo((): DonutChartData[] => {
    // Parse buying intent data from text (same as old component)
    const parseBuyingIntent = (intentText: string) => {
      if (!intentText || intentText === 'N/A') return [];
      
      // Don't clean the input text - it might destroy the parentheses format!
      const cleanedText = String(intentText).trim();
      
      const regex = /([^(,]+)\s*\((\d+)\)/g;
      const results = [];
      let match;
      
      while ((match = regex.exec(cleanedText)) !== null) {
        const topic = match[1].trim();
        const count = 1; // ✅ CHANGE: Always count as 1, ignore the number in parentheses
        if (topic) {
          results.push({ topic, count });
        }
      }
      
      return results;
    };

    // Use centralized getFieldValue function

    const frequency = new Map<string, number>();
    // Track which companies have been counted for each keyword (to ensure each company counts as 1)
    const keywordCompanyMap = new Map<string, Set<string>>();
    let total = 0;

    // Helper to get company name from row
    const getCompanyName = (row: any): string | null => {
      return getFieldValue(row, [
        'Company Name', 'Company', 'CompanyName', 'company_name',
        'Organization', 'Organization Name', 'Business Name',
        'company', 'organization', 'business_name'
      ]);
    };

    data.forEach(row => {
      if (groupBy === 'intent') {
        // For intent grouping, aggregate by actual buying intent topics
        // Each company should count as 1, regardless of how many leads it has
        const companyName = getCompanyName(row);
        if (!companyName) return; // Skip if no company name
        
        const buyingIntentColumns = [
          ...getBuyingIntentColumns(timeRange)
        ];
        
        buyingIntentColumns.forEach(column => {
          const value = row[column];
          if (value && value !== 'N/A' && value !== '') {
            const intentData = parseBuyingIntent(String(value));
            intentData.forEach(({ topic }) => {
              if (topic) {
                // Initialize set for this keyword if it doesn't exist
                if (!keywordCompanyMap.has(topic)) {
                  keywordCompanyMap.set(topic, new Set<string>());
                }
                
                // Only count this company once per keyword
                const companiesForKeyword = keywordCompanyMap.get(topic)!;
                if (!companiesForKeyword.has(companyName)) {
                  companiesForKeyword.add(companyName);
                  frequency.set(topic, (frequency.get(topic) || 0) + 1);
                  total += 1;
                }
              }
            });
          }
        });
      } else {
        // For country/industry grouping
        let groupValue: string | null = null;
        
        if (groupBy === 'country') {
          groupValue = getFieldValue(row, [
            'Company Country'
          ]);
        } else if (groupBy === 'industry') {
          groupValue = getFieldValue(row, [
            'Industry', 'Sector', 'Business Type', 'Company Type',
            'Primary Industry', 'Business Category', 'Industry Category',
            'industry', 'sector', 'business_type', 'company_type'
          ]);
        }
        
        if (groupValue && groupValue !== 'Unknown') {
          frequency.set(groupValue, (frequency.get(groupValue) || 0) + 1);
          total++;
        }
      }
    });

    if (total === 0) return [];

    // Define colors based on theme mode
    const colors = theme.palette.mode === 'dark' 
      ? ['#3981F7', '#C3B5FD', '#51C6FB', '#0836EE'] // Dark mode colors
      : ['#1B4672', '#8B5CF6', '#3B82F6', '#0A112F']; // Light mode colors

    console.log(`🔍 DONUT DEBUG: Final frequency map:`, Array.from(frequency.entries()));
    console.log(`🔍 DONUT DEBUG: Total count: ${total}`);
    
    // Get top 4 items and ensure they total 100%
    const topItems = Array.from(frequency.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
    
    // Calculate percentages that sum to 100%
    const topTotal = topItems.reduce((sum, [, count]) => sum + count, 0);
    let remainingPercentage = 100;
    
    return topItems.map(([label, count], index) => {
      let percentage;
      if (index === topItems.length - 1) {
        // Last item gets the remaining percentage to ensure total = 100%
        percentage = remainingPercentage;
      } else {
        percentage = Math.round((count / topTotal) * 100);
        remainingPercentage -= percentage;
      }
      
      return {
        label,
        value: count,
        percentage: Math.max(1, percentage), // Ensure minimum 1% for visibility
        color: colors[index]
      };
    });
  }, [data, groupBy, timeRange]);


  const getColorClass = (color: string) => {
    const colorMap: { [key: string]: string } = {
      // Dark mode colors
      '#3981F7': 'dashboard-dark-color-blue',
      '#C3B5FD': 'dashboard-dark-color-lightpurple',
      '#51C6FB': 'dashboard-dark-color-lightblue',
      '#0836EE': 'dashboard-dark-color-darkblue',
      // Light mode colors
      '#1B4672': 'dashboard-light-color-blue',
      '#8B5CF6': 'dashboard-light-color-purple',
      '#3B82F6': 'dashboard-light-color-lightblue',
      '#0A112F': 'dashboard-light-color-darkblue'
    };
    return colorMap[color] || (theme.palette.mode === 'dark' ? 'dashboard-dark-color-blue' : 'dashboard-light-color-blue');
  };

  if (donutData.length === 0) {
    return (
      <Box className="dashboard-dark-chart-section">
        <div className="dashboard-dark-section-header">
          <h2>{title}</h2>
        </div>
        <Box className="dashboard-text-center" sx={{ py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  const topPercentage = donutData && donutData.length > 0 ? donutData[0]?.percentage || 0 : 0;

  return (
    <Box className="dashboard-dark-chart-section">
      <div className="dashboard-dark-section-header">
        <h2>{title}</h2>
      </div>
      <div className="dashboard-dark-chart-content">
        <div className="dashboard-dark-donut-container">
          <svg width="275" height="275" viewBox="0 0 44 44" className="donut">
            {/* Inner white/background hole */}
            <circle 
              className="donut-hole" 
              cx="22" 
              cy="22" 
              r="8" 
              fill="#ffffff"
            />
            {/* Data segments */}
            {donutData.map((segment, index) => {
              const radius = 14;
              const circumference = 2 * Math.PI * radius;
              
              // Calculate cumulative offset for proper positioning
              let cumulativePercentage = 0;
              for (let i = 0; i < index; i++) {
                cumulativePercentage += donutData[i].percentage;
              }
              
              const strokeDasharray = `${(segment.percentage / 100) * circumference} ${circumference}`;
              const strokeDashoffset = -((cumulativePercentage / 100) * circumference);
              
              return (
                <circle
                  key={index}
                  className="donut-segment"
                  cx="22"
                  cy="22"
                  r={radius}
                  fill="transparent"
                  stroke={segment.color}
                  strokeWidth="6"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  transform="rotate(-90 22 22)"
                />
              );
            })}
          </svg>
          <span className={`dashboard-dark-donut-percent-${theme.palette.mode}`}>{topPercentage}%</span>
        </div>
        <div className="dashboard-dark-chart-labels">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
            {/* First Row - First 2 keywords */}
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'space-between' }}>
              {donutData.slice(0, 2).map((item, index) => (
                <div key={index} className={`dashboard-chart-label-item ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} style={{ flex: 1 }}>
                  <div 
                    className={`dashboard-color-box ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                    style={{ 
                      borderLeft: `4px solid ${item.color}`,
                      height: '40px',
                      marginRight: '8px'
                    }}
                  ></div>
                  <span 
                    className={`donut-chart-label-text ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                    style={{ letterSpacing: '0.1em', lineHeight: '1.4' }}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Second Row - Next 2 keywords */}
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'space-between' }}>
              {donutData.slice(2, 4).map((item, index) => (
                <div key={index + 2} className={`dashboard-chart-label-item ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} style={{ flex: 1 }}>
                  <div 
                    className={`dashboard-color-box ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} 
                    style={{ 
                      borderLeft: `4px solid ${item.color}`,
                      height: '40px',
                      marginRight: '8px'
                    }}
                  ></div>
                  <span 
                    className={`donut-chart-label-text ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                    style={{ letterSpacing: '0.1em', lineHeight: '1.4' }}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

// Ranked List Chart Component
interface RankedListData {
  label: string;
  value: number;
  percentage: number;
  color: string;
}

const RankedListChart: React.FC<{ 
  data: any[], 
  groupBy: 'country' | 'industry' | 'intent',
  title: string,
  timeRange?: 'last7days' | 'last30days' | 'last90days'
}> = ({ data, groupBy, title, timeRange = 'last7days' }) => {
  const theme = useTheme();
  
  // Process data for ranked list
  const rankedData = useMemo((): RankedListData[] => {

    // Parse buying intent data from text (same as old component)
    const parseBuyingIntent = (intentText: string) => {
      if (!intentText || intentText === 'N/A') return [];
      
      // Clean the input text first using our enhanced cleaning utility - NO pipe separator for ranked list
      const cleanedText = cleanDisplayValue(intentText, 'Buying Intent', false) as string;
      
      const regex = /([^(,]+)\s*\((\d+)\)/g;
      const results = [];
      let match;
      
      while ((match = regex.exec(cleanedText)) !== null) {
        const topic = match[1].trim();
        const count = parseInt(match[2], 10) || 1;
        if (topic) {
          // Use the frequency number from parentheses
          results.push({ topic, count });
        }
      }
      
      return results;
    };

    // Use centralized getFieldValue function

    const frequency = new Map<string, number>();
    let total = 0;

    data.forEach(row => {
      if (groupBy === 'intent') {
        // For intent grouping, aggregate by actual buying intent topics
        const buyingIntentColumns = [
          ...getBuyingIntentColumns(timeRange)
        ];
        
        buyingIntentColumns.forEach(column => {
          const value = row[column];
          if (value && value !== 'N/A' && value !== '') {
            const intentData = parseBuyingIntent(String(value));
            intentData.forEach(({ topic, count }) => {
              if (topic) {
                // Add the frequency number from the keyword
                frequency.set(topic, (frequency.get(topic) || 0) + count);
                total += count;
              }
            });
          }
        });
      } else {
        // For country/industry grouping
        let groupValue: string | null = null;
        
        if (groupBy === 'country') {
          groupValue = getFieldValue(row, [
            'Company Country', 'Headquarters Country', 'HQ Country', 'HQ Location',
            'Address Country', 'Office Location', 'Territory', 'Market', 'Geographic Region',
            'Country Name', 'Country/Region', 'Geographic Location',
            'Country', 'Location', 'Nation', 'State', 'Region',
            'country', 'location', 'nation', 'state', 'region'
          ]);
        } else if (groupBy === 'industry') {
          groupValue = getFieldValue(row, [
            'Industry', 'Sector', 'Business Type', 'Company Type',
            'Primary Industry', 'Business Category', 'Industry Category',
            'industry', 'sector', 'business_type', 'company_type'
          ]);
        }
        
        if (groupValue && groupValue !== 'Unknown') {
          frequency.set(groupValue, (frequency.get(groupValue) || 0) + 1);
          total++;
        }
      }
    });

    if (total === 0) return [];

    // Define colors for ranks - matching reference image
    const colors = ['#f59e0b', '#64748b', '#10b981', '#6b7280'];

    return Array.from(frequency.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4) // Top 4 items
      .map(([label, count], index) => ({
        label,
        value: count,
        percentage: Math.round((count / total) * 100),
        color: colors[index % colors.length]
      }));
  }, [data, groupBy, timeRange]);

  const getRankClass = (index: number) => {
    const rankClasses = ['dashboard-dark-rank-1', 'dashboard-dark-rank-2', 'dashboard-dark-rank-3', 'dashboard-dark-rank-4'];
    return rankClasses[index] || 'dashboard-dark-rank-1';
  };

  const getProgressClass = (index: number) => {
    const progressClasses = ['progress-1', 'progress-2', 'progress-3', 'progress-4'];
    return progressClasses[index] || 'progress-1';
  };

  const getProgressWidth = (value: number, maxValue: number) => {
    return Math.max((value / maxValue) * 100, 2); // Minimum 2% width
  };

  if (rankedData.length === 0) {
    return (
      <Box className="dashboard-dark-list-section">
        {title && (
          <div className="dashboard-dark-section-header">
            <h2>{title}</h2>
          </div>
        )}
        <Box className="dashboard-text-center" sx={{ py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  const maxValue = Math.max(...rankedData.map(item => item.value));

  return (
    <Box className="dashboard-dark-list-section">
      {title && (
        <div className="dashboard-dark-section-header">
          <h2>{title}</h2>
        </div>
      )}
      <div className="dashboard-dark-list-items">
        {rankedData.map((item, index) => (
          <div key={index} className="dashboard-dark-list-item">
            <div className="dashboard-dark-list-item-header">
              <div className={`dashboard-dark-list-rank ${getRankClass(index)}`}>
                {index + 1}
              </div>
              <div className="dashboard-dark-list-content">
                <h3>{item.label}</h3>
              </div>
              <span className="dashboard-dark-list-value">{item.value.toLocaleString()}</span>
            </div>
            <div className="dashboard-dark-progress-bar">
              <div 
                className={`dashboard-dark-progress-fill ${getProgressClass(index)}`}
                style={{ 
                  width: `${getProgressWidth(item.value, maxValue)}%`,
                  backgroundColor: item.color
                }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </Box>
  );
};

// Pie Chart Component for Buying Intent Distribution
interface PieChartData {
  label: string;
  value: number;
  percentage: number;
  color: string;
}

// Helper function to get buying intent columns based on time range (excludes main Buying Intent column)
const getBuyingIntentColumns = (timeRange: string) => {
  switch (timeRange) {
    case 'last7days':
      return ['Last 7 Days'];
    case 'last30days':
      return ['Last 30 Days'];
    case 'last90days':
      return ['Last 90 Days'];
    default:
      return ['Last 90 Days'];
  }
};

// Parse buying intent data from text (for pie chart functionality)
const parseBuyingIntent = (intentText: string): LegacyBuyingIntentItem[] => {
  if (!intentText || intentText === 'N/A') return [];
  
  const results: LegacyBuyingIntentItem[] = [];
  
  // First try the format with numbers: "keyword (number)"
  const regexWithNumbers = /([^(,]+)\s*\((\d+)\)/g;
  let match;
  
  while ((match = regexWithNumbers.exec(intentText)) !== null) {
    const topic = match[1].trim();
    const count = 1; // ✅ CHANGE: Always count as 1, ignore the number in parentheses
    if (topic) {
      results.push({ topic, count });
    }
  }
  
  // If no matches with numbers, try parsing as comma-separated keywords
  if (results.length === 0) {
    const keywords = intentText.split(',').map(k => k.trim()).filter(k => k && k !== 'N/A');
    keywords.forEach(keyword => {
      if (keyword) {
        results.push({ topic: keyword, count: 1 });
      }
    });
  }
  
  return results;
};

const BuyingIntentPieChart: React.FC<{ 
  data: any[], 
  groupBy: 'country' | 'industry' | 'intent',
  title: string,
  timeRange?: 'last7days' | 'last30days' | 'last90days'
}> = ({ data, groupBy, title, timeRange = 'last7days' }) => {
  const theme = useTheme();
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    label: string;
    value: number;
    percentage: number;
  }>({
    visible: false, x: 0, y: 0, label: '', value: 0, percentage: 0
  });

  // Process data for pie chart
  const pieData = useMemo((): PieChartData[] => {
    // Use centralized getFieldValue function

    const groupMap = new Map<string, number>();
    // Track which companies have been counted for each keyword (to ensure each company counts as 1)
    const keywordCompanyMap = new Map<string, Set<string>>();
    
    // Helper to get company name from row
    const getCompanyName = (row: any): string | null => {
      return getFieldValue(row, [
        'Company Name', 'Company', 'CompanyName', 'company_name',
        'Organization', 'Organization Name', 'Business Name',
        'company', 'organization', 'business_name'
      ]);
    };
    
    data.forEach(row => {
      if (groupBy === 'intent') {
        // For intent grouping, aggregate by actual buying intent topics
        // Each company should count as 1, regardless of how many leads it has
        const companyName = getCompanyName(row);
        if (!companyName) return; // Skip if no company name
        
        const buyingIntentColumns = [
          ...getBuyingIntentColumns(timeRange)
        ];
        
        buyingIntentColumns.forEach(column => {
          const value = row[column];
          if (value && value !== 'N/A' && value !== '') {
            const intentData = parseBuyingIntent(String(value));
            intentData.forEach((item: LegacyBuyingIntentItem) => {
              if (item.topic) {
                // Initialize set for this keyword if it doesn't exist
                if (!keywordCompanyMap.has(item.topic)) {
                  keywordCompanyMap.set(item.topic, new Set<string>());
                }
                
                // Only count this company once per keyword
                const companiesForKeyword = keywordCompanyMap.get(item.topic)!;
                if (!companiesForKeyword.has(companyName)) {
                  companiesForKeyword.add(companyName);
                  groupMap.set(item.topic, (groupMap.get(item.topic) || 0) + 1);
                }
              }
            });
          }
        });
      } else {
        // For country/industry grouping
        let groupValue: string;
        
        if (groupBy === 'country') {
          groupValue = getFieldValue(row, [
            'Country', 'Location', 'Nation', 'State', 'Region',
            'Country Name', 'Country/Region', 'Geographic Location',
            'Headquarters Country', 'HQ Country', 'HQ Location',
            'Address Country', 'Company Country', 'Office Location'
          ]);
        } else {
          groupValue = getFieldValue(row, [
            'Company Industry', 'Industry', 'Business Industry', 
            'Company Sector', 'Sector', 'Business Sector'
          ]);
        }
        
        // Count buying intent keywords for this row
        const buyingIntentColumns = [
          ...getBuyingIntentColumns(timeRange)
        ];
        
        let keywordCount = 0;
        buyingIntentColumns.forEach(column => {
          const value = row[column];
          if (value && value !== 'N/A' && value !== '') {
            const intentData = parseBuyingIntent(String(value));
            // Sum the frequency counts, not just count unique keywords
            keywordCount += intentData.reduce((sum, item) => sum + item.count, 0);
          }
        });
        
        if (keywordCount > 0) {
          groupMap.set(groupValue, (groupMap.get(groupValue) || 0) + keywordCount);
        }
      }
    });

    const total = Array.from(groupMap.values()).reduce((sum, count) => sum + count, 0);
    
    if (total === 0) return [];

    const colors = [
      '#2E7D32', '#1565C0', '#6A1B9A', '#D84315', '#424242',
      '#388E3C', '#1976D2', '#7B1FA2', '#1B5E20', '#0D47A1',
      '#4A148C', '#BF360C', '#212121', '#2E7D32', '#1565C0'
    ];

    return Array.from(groupMap.entries())
      .map(([label, value], index) => ({
        label: label.length > 15 ? label.substring(0, 15) + '...' : label,
        value,
        percentage: (value / total) * 100,
        color: colors[index % colors.length]
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8); // Top 8 segments
  }, [data, groupBy, timeRange]);

  // Calculate angles for pie segments
  const segments = useMemo(() => {
    let currentAngle = -90; // Start from top
    return pieData.map((item, index) => {
      const startAngle = currentAngle;
      const endAngle = currentAngle + (item.percentage / 100) * 360;
      currentAngle = endAngle;
      
      const midAngle = (startAngle + endAngle) / 2;
      const radius = 80;
      const centerX = 110;
      const centerY = 110;
      
      // Calculate label position
      const labelRadius = radius + 20;
      const labelX = centerX + Math.cos((midAngle * Math.PI) / 180) * labelRadius;
      const labelY = centerY + Math.sin((midAngle * Math.PI) / 180) * labelRadius;
      
      return {
        ...item,
        startAngle,
        endAngle,
        midAngle,
        labelX,
        labelY,
        index
      };
    });
  }, [pieData]);

  const createArcPath = (centerX: number, centerY: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle);
    const end = polarToCartesian(centerX, centerY, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    
    return [
      "M", centerX, centerY,
      "L", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const handleMouseEnter = (segment: any, event: React.MouseEvent) => {
    setHoveredSegment(segment.index);
    setTooltip({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      label: segment.label,
      value: segment.value,
      percentage: segment.percentage
    });
  };

  const handleMouseLeave = () => {
    setHoveredSegment(null);
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: event.clientX,
        y: event.clientY
      }));
    }
  };

  if (pieData.length === 0) {
    return (
      <Card sx={{ 
        p: 2, 
        mt: 2, 
        height: 250,
        backgroundColor: '#6219823d',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '30px'
      }}>
        <Typography variant="subtitle1" gutterBottom sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: theme.palette.text.secondary,
          fontSize: '0.9rem'
        }}>
          <PieChartIcon sx={{ fontSize: 18 }} />
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No {groupBy === 'intent' ? 'buying intent' : 'distribution'} data available
        </Typography>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ 
        p: 2, 
        mt: 2, 
        height: 280,
        backgroundColor: theme.palette.mode === 'dark' ? '#5C445A' : '#ffffff',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '30px',
        boxShadow: theme.shadows[2],
        position: 'relative',
        overflow: 'visible'
      }}
      onMouseMove={handleMouseMove}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            fontSize: '0.9rem'
          }}>
            <PieChartIcon sx={{ fontSize: 18 }} />
            {title}
          </Typography>
          <Chip 
            label={`${pieData.length} ${groupBy === 'country' ? 'countries' : groupBy === 'industry' ? 'industries' : 'intent topics'}`} 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: 200 }}>
          {/* Pie Chart SVG */}
          <Box sx={{ position: 'relative' }}>
            <svg width="220" height="220" viewBox="0 0 220 220">
              {/* Pie segments */}
              {segments.map((segment) => {
                const isHovered = hoveredSegment === segment.index;
                const radius = isHovered ? 85 : 80;
                
                return (
                  <g key={segment.index}>
                    {/* Segment */}
                    <path
                      d={createArcPath(110, 110, radius, segment.startAngle, segment.endAngle)}
                      fill={segment.color}
                      stroke="white"
                      strokeWidth="2"
                      style={{
                        filter: isHovered ? `drop-shadow(0 0 8px ${segment.color}66)` : 'none',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => handleMouseEnter(segment, e as any)}
                      onMouseLeave={handleMouseLeave}
                    />
                    
                    {/* Value labels on segments */}
                    {segment.percentage > 5 && (
                      <text
                        x={110 + Math.cos((segment.midAngle * Math.PI) / 180) * 50}
                        y={110 + Math.sin((segment.midAngle * Math.PI) / 180) * 50}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize="11"
                        fontWeight="bold"
                        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)', pointerEvents: 'none' }}
                      >
                        {segment.value}
                      </text>
                    )}
                  </g>
                );
              })}
              
              {/* Center circle */}
              <circle
                cx="110"
                cy="110"
                r="25"
                fill={theme.palette.background.paper}
                stroke={theme.palette.divider}
                strokeWidth="2"
              />
              
              {/* Center text */}
              <text
                x="110"
                y="105"
                textAnchor="middle"
                dominantBaseline="middle"
                fill={theme.palette.text.primary}
                fontSize="10"
                fontWeight="bold"
              >
                Total
              </text>
              <text
                x="110"
                y="118"
                textAnchor="middle"
                dominantBaseline="middle"
                fill={theme.palette.primary.main}
                fontSize="12"
                fontWeight="bold"
              >
                {pieData.reduce((sum, item) => sum + item.value, 0)}
              </text>
            </svg>
          </Box>
          
          {/* Legend */}
          <Box sx={{ flex: 1, maxHeight: 200, overflow: 'auto' }}>
            {segments.map((segment, index) => (
              <Box 
                key={segment.index}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: 1,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: hoveredSegment === segment.index ? 'action.hover' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: 0,
                  animation: `fadeInUp 0.4s ease-out ${index * 0.1}s forwards`,
                  '@keyframes fadeInUp': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
                onMouseEnter={(e) => handleMouseEnter(segment, e)}
                onMouseLeave={handleMouseLeave}
              >
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    backgroundColor: segment.color,
                    borderRadius: '50%',
                    boxShadow: hoveredSegment === segment.index ? `0 0 8px ${segment.color}66` : 'none',
                    transition: 'all 0.3s ease'
                  }} 
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '0.7rem',
                      color: theme.palette.text.primary,
                      overflow: 'visible',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: 1.2
                    }}
                    title={segment.label}
                  >
                    {segment.label}
                  </Typography>
                </Box>

              </Box>
            ))}
          </Box>
        </Box>
        
        {/* Chart Summary */}
        <Box sx={{ 
          mt: 1, 
          pt: 1, 
          borderTop: `1px solid ${theme.palette.divider}`,
          textAlign: 'center'
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
            {groupBy === 'intent' ? 'Intent topic distribution' : `Intent distribution by ${groupBy}`} � {pieData.reduce((sum, item) => sum + item.value, 0)} total {groupBy === 'intent' ? 'occurrences' : 'keywords'}
          </Typography>
        </Box>
      </Card>

      {/* Interactive Tooltip */}
      {tooltip.visible && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltip.x + 10,
            top: tooltip.y - 60,
            zIndex: 9999,
            background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 1.5,
            boxShadow: theme.shadows[8],
            pointerEvents: 'none',
            animation: 'tooltipFadeIn 0.2s ease-out'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.8rem' }}>
            {tooltip.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
            {groupBy === 'intent' ? 'Occurrences' : 'Keywords'}: {tooltip.value}
          </Typography>

        </Box>
      )}
    </>
  );
};

// Company-specific Buying Intent Chart Component
const CompanyBuyingIntentChart: React.FC<{ company: CompanyData }> = ({ company }) => {
  const theme = useTheme();
  
  // Parse buying intent from company's data
  const chartData = useMemo(() => {
    const keywordFrequency = new Map<string, number>();
    
    company.allData.forEach(row => {
      // Check if there's a standalone "Buying Intent" column first
      const standaloneBuyingIntent = row['Buying Intent'];
      if (standaloneBuyingIntent && standaloneBuyingIntent !== 'N/A' && standaloneBuyingIntent !== '') {
        const regex = /([^(,]+)\s*\((\d+)\)/g;
        let match;
        while ((match = regex.exec(standaloneBuyingIntent)) !== null) {
          const topic = match[1].trim();
          const count = 1; // ✅ CHANGE: Always count as 1, ignore the number in parentheses
          if (topic) {
            keywordFrequency.set(topic, (keywordFrequency.get(topic) || 0) + count);
          }
        }
      } else {
        // If no standalone "Buying Intent" column, aggregate from time-based columns
        const buyingIntentColumns = [
          'Last 7 Days',
          'Last 30 Days',
          'Last 90 Days'
        ];
        
        buyingIntentColumns.forEach(column => {
          const value = row[column];
          if (value && value !== 'N/A' && value !== '') {
            const regex = /([^(,]+)\s*\((\d+)\)/g;
            let match;
            while ((match = regex.exec(value)) !== null) {
              const topic = match[1].trim();
              const count = 1; // ✅ CHANGE: Always count as 1, ignore the number in parentheses
              if (topic) {
                keywordFrequency.set(topic, (keywordFrequency.get(topic) || 0) + count);
              }
            }
          }
        });
      }
    });
    
    const maxCount = Math.max(...Array.from(keywordFrequency.values()), 1);
    
    return Array.from(keywordFrequency.entries())
      .map(([keyword, count]) => ({
        keyword,
        count,
        percentage: (count / maxCount) * 100
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [company]);

  const getBarColor = (index: number) => {
    const colors = [
      '#2E7D32', '#1565C0', '#6A1B9A', '#D84315', '#424242',
      '#388E3C', '#1976D2', '#7B1FA2'
    ];
    return colors[index % colors.length];
  };

  if (chartData.length === 0) {
    return (
      <Box sx={{ 
        p: 3, 
        textAlign: 'center',
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="body2" color="text.secondary">
          No buying intent data available for this company
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`
    }}>
      <Typography variant="h6" gutterBottom sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        mb: 2
      }}>
        <BarChartIcon />
        Buying Intent Distribution
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {chartData.map((item, index) => (
          <Box key={item.keyword} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ minWidth: 120, maxWidth: 120 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'medium',
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={item.keyword}
              >
                {item.keyword}
              </Typography>
            </Box>
            
            <Box sx={{ flex: 1, position: 'relative', height: 24 }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${Math.min(item.percentage, 85)}%`, // Limit bar width to prevent overlap
                  backgroundColor: getBarColor(index),
                  borderRadius: 1.5,
                  transition: 'all 0.3s ease',
                  minWidth: item.count > 0 ? '20px' : 0,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                  zIndex: 1,
                  minWidth: '20px',
                  textAlign: 'right',
                }}
              >
                {item.count}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Helper function to get merged name from first and last name
const getMergedName = (lead: any): string => {
  const firstName = lead['First Name'];
  const lastName = lead['Last Name'];
  const name = lead['Name'];
  
  // If Name column has a value, use it
  if (name && name !== 'N/A' && name !== '') {
    return name;
  }
  
  // Otherwise, merge First Name and Last Name
  const hasFirstName = firstName && firstName !== 'N/A' && firstName !== '';
  const hasLastName = lastName && lastName !== 'N/A' && lastName !== '';
  
  if (hasFirstName && hasLastName) {
    return `${firstName} ${lastName}`;
  } else if (hasFirstName) {
    return firstName;
  } else if (hasLastName) {
    return lastName;
  }
  
  return 'N/A';
};

// This will be defined inside the component to access theme
let cleanDisplayValueLocal: (value: any, columnName: string, lead?: any) => React.ReactNode;

// Using centralized robust column matching from utils

const ClientDashboard: React.FC = () => {
  const theme = useTheme();
  
  // Helper function to safely get dashboard colors with fallbacks
  const getDashboardColor = (type: 'firstRow' | 'secondRow') => {
    if (type === 'firstRow') {
      return theme.palette.dashboard?.firstRowBg || (theme.palette.mode === 'dark' ? '#302038' : '#E8E2F3');
    } else {
      return theme.palette.dashboard?.secondRowBg || (theme.palette.mode === 'dark' ? '#5C445A' : '#F0E6F7');
    }
  };
  
  const { user } = useAuth();
  const { username } = useParams<{ username: string }>();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [uploadedData, setUploadedData] = useState<UploadedData | null>(null);
  const [buyingIntentData, setBuyingIntentData] = useState<BuyingIntentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTemporaryAccess, setIsTemporaryAccess] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [expandedLeadRow, setExpandedLeadRow] = useState<number | null>(null);
  const [buyingIntentTimeRange, setBuyingIntentTimeRange] = useState<string>('30');
  const [selectedCountryFilter, setSelectedCountryFilter] = useState<string>('All Countries');
  const [companyLeadsFilter, setCompanyLeadsFilter] = useState<string[]>([]);
  const [mainTableColumnWidths, setMainTableColumnWidths] = useState<{ [key: string]: number }>({});
  const [popupTableColumnWidths, setPopupTableColumnWidths] = useState<{ [key: string]: number }>({});
  const [autoFittedColumns, setAutoFittedColumns] = useState<{ [key: string]: boolean }>({});
  const [detailTableColumnWidths, setDetailTableColumnWidths] = useState<{ [key: string]: number }>({});
  const [selectedTimeRange, setSelectedTimeRange] = useState<'last7days' | 'last30days' | 'last90days'>('last7days');
  
  // Country-specific search and filter states
  const [countryFilters, setCountryFilters] = useState<{ [country: string]: CountryFilterState }>({});
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<{ [country: string]: HTMLElement | null }>({});
  const [expandedFilters, setExpandedFilters] = useState<{ [country: string]: boolean }>({});
  
  // Country sorting state
  const [countrySortType, setCountrySortType] = useState<'alphabetical' | 'numerical' | 'latestUpdated'>('numerical');
  const [numericalSortOrder, setNumericalSortOrder] = useState<'desc' | 'asc'>('desc');
  
  // Website engagement filter state
  const [showOnlyWebsiteEngagement, setShowOnlyWebsiteEngagement] = useState(false);
  
  // Animation state
  const [animationLoaded, setAnimationLoaded] = useState(false);
  
  // Define cleanDisplayValueLocal inside component to access theme
  cleanDisplayValueLocal = (value: any, columnName: string, lead?: any): React.ReactNode => {
    // Special handling for Name column
    if (columnName === 'Name' && lead) {
      const mergedName = getMergedName(lead);
      return mergedName;
    }
    
    // Check if this is a domain/website column and make it clickable
    const columnLower = columnName.toLowerCase();
    const isDomainColumn = columnLower.includes('domain') || columnLower.includes('website') || columnLower.includes('url') || columnLower.includes('web');
    
    if (isDomainColumn && value && typeof value === 'string' && value.trim() !== '' && value !== 'N/A') {
      // Check if value looks like a URL/domain
      const valueStr = value.trim();
      const isUrl = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=@#]*)?$/i.test(valueStr) || 
                    /^([\w-]+\.)+[\w-]+(\/[\w-./?%&=@#]*)?$/i.test(valueStr);
      
      if (isUrl) {
        // Import linkifyText dynamically to avoid circular dependencies
        const { linkifyText } = require('../../utils/linkUtils');
        return linkifyText(valueStr, { 
          color: theme.palette.primary.main,
          underline: 'hover'
        });
      }
    }
    
    // Use the enhanced cleaning utility for all other columns
    return cleanDisplayValue(value, columnName);
  };

  // Use Sheet 1 data only (server filters this based on ?sheet=1 parameter)
  const allData = useMemo(() => {
    if (!uploadedData?.sheets || uploadedData.sheets.length === 0) return [];
    
    // Since we're calling /api/upload/client/data?sheet=1, we should only get Sheet 1
    const sheet1 = uploadedData.sheets[0]; // First (and should be only) sheet from server
    
    
    if (!sheet1) {
      console.log('❌ No Sheet 1 found. Available sheets:', uploadedData.sheets.map((s: SheetData) => ({ 
        fileName: s.fileName, 
        originalFileName: s.originalFileName, 
        fileType: s.fileType 
      })));
      return [];
    }
    
    console.log('✅ Using Sheet 1:', sheet1.originalFileName, 'with', sheet1.data?.length, 'rows');
    console.log('🔍 DEBUG: Sheet 1 fileType:', sheet1.fileType);
    console.log('🔍 DEBUG: Sheet 1 recordCount:', sheet1.recordCount);
    
    // Return data from Sheet 1, filtered to remove empty rows
    const sheetData = sheet1.data || [];
    
    // Filter out rows that have no meaningful data (all empty/null/N/A values)
    const filteredData = sheetData.filter(row => {
      // Check if row has at least one meaningful value
      const hasData = Object.values(row).some(value => {
        if (value === null || value === undefined || value === '') return false;
        if (typeof value === 'string' && (value.trim() === '' || value.trim() === 'N/A')) return false;
        return true;
      });
      return hasData;
    });
    
    console.log('✅ Filtered Sheet 1 data:', filteredData.length, 'meaningful rows');
    console.log('🔍 DEBUG: Final allData length:', filteredData.length);
    return filteredData;
  }, [uploadedData]);

  // Get all unique columns from Sheet 1 only (server returns only Sheet 1)
  const allColumns = useMemo(() => {
    if (!uploadedData?.sheets || uploadedData.sheets.length === 0) return [];
    
    const columnsSet = new Set<string>();
    const excludeColumns = ['_completedAt', '_isCompleted', '_uploadVersion', '_uploadId', 'version', 'Keyword', 'Category', 'Label']; // Hide internal tracking, system columns, and buying intent columns
    
    // Get Sheet 1 (lead data) - server already filtered to Sheet 1 only
    const sheet1 = uploadedData.sheets[0]; // First (and only) sheet from server
    
    if (!sheet1) return [];
    
    // Add columns from Sheet 1 metadata only
    sheet1.columns.forEach(column => {
      if (!excludeColumns.includes(column)) {
        columnsSet.add(column);
      }
    });
    
    // Trust API-pruned columns only to avoid leaking columns from other clients
    // (Do not augment with Object.keys from sample rows)
    
    // Filter out duplicate buying intent columns - keep only standardized versions
    const allCols = Array.from(columnsSet);
    const standardizedBuyingIntentColumns = [
      'Last 7 Days',
      'Last 30 Days',
      'Last 90 Days'
    ];
    
    // Remove all buying intent column variations EXCEPT the standardized ones
    const filteredColumns = allCols.filter(col => {
      const lower = col.toLowerCase();
      // If this is one of the standardized columns, keep it
      if (standardizedBuyingIntentColumns.includes(col)) return true;
      
      // Filter out any other buying intent variations (with parentheses, different brackets, etc.)
      const isBuyingIntentVariation = (
        lower === 'buying intent' || // Remove standalone "Buying Intent" column
        lower === 'data intent' || // Remove standalone "Data Intent" column
        (lower.includes('buying') && lower.includes('intent') && 
         (lower.includes('7') || lower.includes('30') || lower.includes('90'))) ||
        (lower.includes('last') && (lower.includes('7') || lower.includes('30') || lower.includes('90')) &&
         (lower.includes('day') || lower.includes('intent')))
      );
      
      return !isBuyingIntentVariation;
    });
    
    // Sort columns to put Last 7/30/90 Days columns at the end
    const timeColumns = ['Last 7 Days', 'Last 30 Days', 'Last 90 Days'];
    const otherColumns = filteredColumns.filter(col => !timeColumns.includes(col));
    const existingTimeColumns = timeColumns.filter(col => filteredColumns.includes(col));
    
    return [...otherColumns, ...existingTimeColumns];
  }, [uploadedData]);

  // Analytics data is just all the data
  const analyticsData = allData;

  // Fetch buying intent data
  const fetchBuyingIntentData = async () => {
    try {
      // Let the api interceptor handle the token automatically
      const response = await api.get('/api/upload/client/buying-intent');

      if (response.data.success) {
        setBuyingIntentData(response.data.data);
      } else {
        setBuyingIntentData({ hasData: false, buyingIntents: [], message: response.data.message || 'No buying intent data available' });
      }
    } catch (error: any) {
      console.error('Error fetching buying intent data:', error);
      setBuyingIntentData({ hasData: false, buyingIntents: [], message: 'Error fetching buying intent data' });
    }
  };

  // Get list of available countries from merged data
  const availableCountries = useMemo(() => {
    // Use centralized getFieldValue function

    const countriesSet = new Set<string>();
    analyticsData.forEach(row => {
      const country = getFieldValue(row, [
        'Company Country'
      ]);
      if (country) {
        countriesSet.add(country);
      }
    });
    
    return ['All Countries', ...Array.from(countriesSet).sort()];
  }, [analyticsData]);


  // Force re-render when selectedCountryFilter changes
  const [filterKey, setFilterKey] = useState(0);
  useEffect(() => {
    setFilterKey(prev => prev + 1);
  }, [selectedCountryFilter]);

  // Country name abbreviations for search placeholder
  const getCountryDisplayName = (countryName: string) => {
    const abbreviations: { [key: string]: string } = {
      'United Arab Emirates': 'UAE',
      'United States of America': 'USA',
      'United States': 'USA',
      'United Kingdom': 'UK',
      'South Korea': 'South Korea',
      'Saudi Arabia': 'Saudi Arabia',
      'New Zealand': 'New Zealand',
      'South Africa': 'South Africa',
      'Czech Republic': 'Czech Republic',
      'Dominican Republic': 'Dominican Republic'
    };
    
    return abbreviations[countryName] || countryName;
  };

  // Filter data based on selected country
  const filteredAnalyticsData = useMemo(() => {
    if (selectedCountryFilter === 'All Countries') {
      return analyticsData;
    }

    // Use centralized getFieldValue function

    return analyticsData.filter(row => {
      const country = getFieldValue(row, [
        'Company Country', 'Headquarters Country', 'HQ Country', 'HQ Location',
        'Address Country', 'Office Location', 'Territory', 'Market', 'Geographic Region',
        'Country Name', 'Country/Region', 'Geographic Location',
        'Country', 'Location', 'Nation', 'State', 'Region'
      ]);
      return country === selectedCountryFilter;
    });
  }, [analyticsData, selectedCountryFilter]);

  // Parse buying intent data from text (for analytics calculations)
const parseBuyingIntentForAnalytics = (intentText: string): BuyingIntentItem[] => {
      if (!intentText || intentText === 'N/A') return [];
    
    const results: BuyingIntentItem[] = [];
    
    // Debug logging
    console.log('🔍 DEBUG: Parsing intent text:', intentText);
    
    // First try the format with numbers: "keyword (number)"
    const regexWithNumbers = /([^(,]+)\s*\((\d+)\)/g;
    let match;
    
    while ((match = regexWithNumbers.exec(intentText)) !== null) {
      const topic = match[1].trim();
      const count = 1; // ✅ CHANGE: Always count as 1, ignore the number in parentheses
      console.log(`🔍 DEBUG: Found keyword with count: "${topic}" (${count})`);
      if (topic) {
        results.push({ topic, count });
      }
    }
    
    // If no matches with numbers, try parsing as comma-separated keywords
    if (results.length === 0) {
      console.log('🔍 DEBUG: No parentheses format found, trying comma-separated');
      const keywords = intentText.split(',').map(k => k.trim()).filter(k => k && k !== 'N/A');
      keywords.forEach(keyword => {
        console.log(`🔍 DEBUG: Found keyword without count: "${keyword}" (1)`);
        if (keyword) {
          results.push({ topic: keyword, count: 1 });
        }
      });
    }
    
    console.log('🔍 DEBUG: Final parsed results:', results);
    return results;
  };
  
  // Debug: Log when this component renders


  // Calculate comprehensive insurance analytics with corrected Brand Engagement logic
  const insuranceAnalytics = useMemo((): InsuranceAnalytics => {
    console.log('🚀 STARTING Brand Engagement Calculation in DashboardViewer - RUN #', Math.random().toString(36).substr(2, 9));
    console.log('📊 uploadedData:', uploadedData ? 'Available' : 'Not Available');
    console.log('📊 buyingIntentData:', buyingIntentData ? 'Available' : 'Not Available');
    console.log('📊 filteredAnalyticsData length:', filteredAnalyticsData.length);
    console.log('📊 user?.companyName:', user?.companyName);

    
    if (!filteredAnalyticsData.length && (!buyingIntentData?.hasData || !buyingIntentData.buyingIntents.length)) {
      console.log('❌ No data available for Brand Engagement calculation');
      return {
        totalLeads: 0,
        totalInterestedLeads: 0,
        totalGenericBuyingIntents: 0,
        totalBrandedBuyingIntents: 0,
        timesClientBrandSearched: 0,
        competitorBrands: 0,
        websiteVisitors: 0,
        topIndustries: [],
        topJobPositions: [],
        intent7DaysCount: 0,
        intent30DaysCount: 0,
        intent90DaysCount: 0,
        topIntent7Days: 'No Data',
        topIntent30Days: 'No Data',
        topIntent90Days: 'No Data',
        totalBuyingIntent7Days: 0,
        totalBuyingIntent30Days: 0,
        totalBuyingIntent90Days: 0,
        overallAverageBuyingIntent: 0,
        primaryInterests: ['No Data']
      };
    }

    const totalLeads = filteredAnalyticsData.length;
    
    // Initialize counters and maps
    let totalInterestedLeads = 0;
    const intent7DaysMap = new Map<string, number>();
    const intent30DaysMap = new Map<string, number>();
    const intent90DaysMap = new Map<string, number>();
    const allIntentMap = new Map<string, number>();
    const industryMap = new Map<string, number>();
    const jobTitleMap = new Map<string, number>();
    const companiesWithWebsite = new Set<string>();
    
    const isConnectedInterested = (value: any) => {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return false;
      }

      const normalized = String(value)
        .toLowerCase()
        .replace(/[–—]/g, '-') // normalize en/em dashes
        .replace(/_/g, '-') // treat underscores same as hyphen
        .trim();

      const withoutSpaces = normalized.replace(/\s+/g, '-');

      return withoutSpaces === 'connected-interested';
    };
    
    let totalAverageSum = 0;
    let validAverageCount = 0;

    // Use centralized getFieldValue function (imported from utils)

    // Get client company name for brand search counting
    const clientCompanyName = user?.companyName || 'Unknown Company';

    // CORRECTED BRAND ENGAGEMENT LOGIC
    // Step 1: Extract branded keywords from Sheet 2 (buying intent data)
    const brandedKeywords = new Map<string, 'client' | 'competitor' | 'unclassified'>();
    let totalGenericBuyingIntents = 0;
    let totalBrandedBuyingIntents = 0;

    console.log('🔍 DEBUG: Brand Engagement Analysis');
    console.log('📊 Buying Intent Data:', buyingIntentData?.hasData ? 'Available' : 'Not Available');
    console.log('📊 Filtered Analytics Data Length:', filteredAnalyticsData.length);
    console.log('🏢 Client Company Name:', clientCompanyName);

    // SIMPLE TEST: Check if we can find ANY Lockton-related data
    console.log('🧪 SIMPLE TEST: Looking for Lockton in data...');
    let locktonFound = 0;
    filteredAnalyticsData.slice(0, 5).forEach((row: any, index: number) => {
      const companyName = getFieldValue(row, ['Company Name', 'company_name', 'Company']) || '';
      const last7Days = getFieldValue(row, ['Last 7 Days']) || '';
      const last30Days = getFieldValue(row, ['Last 30 Days']) || '';
      const last90Days = getFieldValue(row, ['Last 90 Days']) || '';
      
      if (companyName.toLowerCase().includes('lockton') || 
          last7Days.toLowerCase().includes('lockton') ||
          last30Days.toLowerCase().includes('lockton') ||
          last90Days.toLowerCase().includes('lockton')) {
        locktonFound++;
        console.log(`🎯 FOUND LOCKTON in row ${index}:`, {
          companyName,
          last7Days: last7Days.substring(0, 100),
          last30Days: last30Days.substring(0, 100),
          last90Days: last90Days.substring(0, 100)
        });
      }
    });
    console.log(`🧪 SIMPLE TEST RESULT: Found Lockton in ${locktonFound} out of first 5 rows`);

    if (buyingIntentData?.hasData) {
      console.log('📋 Total Buying Intents:', buyingIntentData.buyingIntents.length);
      
      // SIMPLE TEST: Check what's in buying intent data
      console.log('🧪 BUYING INTENT TEST: First 3 intents:', buyingIntentData.buyingIntents.slice(0, 3).map((intent: any) => ({
        keyword: intent.keyword,
        category: intent.category,
        label: intent.label
      })));
      
      buyingIntentData.buyingIntents.forEach((intent: any) => {
        const category = (intent.category || '').toLowerCase().trim();
        const keyword = (intent.keyword || '').trim();
        const labelRaw = (intent.label || '').trim();
        const normalizedLabel = labelRaw.toLowerCase();
        
        // Count generic vs branded searches
        if (category.includes('generic') || category.includes('general')) {
          totalGenericBuyingIntents++;
        } else if (category.includes('brand') || category.includes('company')) {
          totalBrandedBuyingIntents++;
        }

        if ((intent.category === 'Branded' || category === 'branded') && keyword) {
          const normalizedKeywordKey = normalizeKeywordForMatch(keyword);
          if (!normalizedKeywordKey) return;

          if (normalizedLabel === 'client' || normalizedLabel === 'competitor') {
            brandedKeywords.set(normalizedKeywordKey, normalizedLabel as 'client' | 'competitor');
            console.log(`🏷️ Branded Keyword: "${keyword}" -> Label: "${normalizedLabel}"`);
          } else {
            brandedKeywords.set(normalizedKeywordKey, 'unclassified');
            console.log(`🟡 Branded keyword "${keyword}" stored as 'unclassified' (label provided: "${labelRaw || 'N/A'}").`);
          }
        }
      });
    }

    // Fallback: If no branded keywords were provided in Sheet 2, infer brand keywords
    // so that empty Label/Branded columns do not zero-out engagement boxes.
    if (brandedKeywords.size === 0) {
      const makeVariants = (name: string) => {
        const lower = name.toLowerCase().trim();
        const noPunct = lower.replace(/[^a-z0-9\s]/g, '').trim();
        const noSpaces = noPunct.replace(/\s+/g, '');
        const acronym = (name.match(/[A-Z]/g) || []).join('').toLowerCase();
        const variants = new Set<string>([lower, noPunct, noSpaces]);
        if (acronym.length >= 2) variants.add(acronym);
        return Array.from(variants).filter(v => v && v.length >= 2);
      };

      const companyVariants = makeVariants(clientCompanyName);
      companyVariants.forEach(v => brandedKeywords.set(v, 'client'));

      console.log('🛟 Fallback branded keywords (inferred from company name):', companyVariants);
    }

    console.log('📈 Branded Keywords Used:', Array.from(brandedKeywords.entries()));
    console.log('📊 Total Branded Keywords Used:', brandedKeywords.size);
    console.log('📋 Client Branded Keywords:', Array.from(brandedKeywords.entries()).filter(([_, label]) => label === 'client').length);
    console.log('🔴 Competitor Branded Keywords:', Array.from(brandedKeywords.entries()).filter(([_, label]) => label === 'competitor').length);
    console.log('🟡 Unclassified Branded Keywords:', Array.from(brandedKeywords.entries()).filter(([_, label]) => label === 'unclassified').length);

    // Step 2: Compare Sheet 1 Lead Data with Sheet 2 Branded Keywords
    let clientBrandEngagement = 0;
    let competitorBrandEngagement = 0;
    // Track per-bucket unique companies to avoid mutual exclusion
    const processedClientCompanies = new Set<string>();
    const processedCompetitorCompanies = new Set<string>();

    console.log('🔄 Starting Sheet 1 vs Sheet 2 comparison...');

    // Process each lead from Sheet 1
    filteredAnalyticsData.forEach((lead: any, leadIndex: number) => {
      const companyName = getFieldValue(lead, ['Company Name', 'company_name', 'Company']) || '';
      const normalizedCompanyName = companyName.toLowerCase().trim();
      
      if (!normalizedCompanyName) {
        return; // Skip if no company name
      }
      
      // Check all intent columns from Sheet 1
      const intentColumns = [
        'Last 7 Days',
        'Last 30 Days', 
        'Last 90 Days',
        'Buying Intent Last 7 Days',
        'Buying Intent Last 30 Days', 
        'Buying Intent Last 90 Days',
        'Buying Intent',
        'Data Intent Last 7 Days',
        'Data Intent Last 30 Days',
        'Data Intent Last 90 Days',
        'Data Intent'
      ];
      
      let hasClientKeyword = false;
      let hasCompetitorKeyword = false;
      let foundMatches: string[] = [];
      
      // Check each intent column for branded keywords
      intentColumns.forEach(column => {
        const intentValue = getFieldValue(lead, [column]) || '';
        if (!intentValue) return;
        
        console.log(`📋 Company: "${companyName}", Column: "${column}", Value: "${intentValue}"`);
        
        // Parse intent data (handle both single values and arrays)
        // Clean the intent value first - remove numbers in parentheses like "Keyword (5)"
        let cleanedIntentValue = String(intentValue).trim();
        cleanedIntentValue = cleanedIntentValue.replace(/\s*\(\d+\)/g, ''); // Remove "(5)", "(133)", etc.
        cleanedIntentValue = cleanedIntentValue.replace(/\(\s*\)/g, ''); // Remove empty parentheses
        
        // Support multiple delimiters: comma, semicolon, pipe, newline
        const intentItems = Array.isArray(cleanedIntentValue)
          ? cleanedIntentValue
          : cleanedIntentValue.split(/[;,|\n]+/).map((s: string) => s.trim()).filter(Boolean);
        
        intentItems.forEach((item: string) => {
          // Clean the item: remove punctuation, trim whitespace
          let cleanedItem = item.trim();
          cleanedItem = cleanedItem.replace(/[.,!?;:]+$/g, ''); // Remove trailing punctuation
          cleanedItem = cleanedItem.replace(/^[.,!?;:]+/g, ''); // Remove leading punctuation
          cleanedItem = cleanedItem.trim();
          const normalizedItem = cleanedItem.toLowerCase();
          
          // Helper function to normalize strings for better matching
          const normalizeForMatching = (str: string) => {
            return str
              .toLowerCase()
              .trim()
              .replace(/[.,!?;:'"()]/g, '')  // Remove punctuation
              .replace(/\s+/g, ' ')           // Normalize spaces
              .replace(/\binc\.?\b|\bllc\.?\b|\bcorp\.?\b|\bcorporation\.?\b|\bltd\.?\b|\blimited\.?\b|\bcompany\.?\b/gi, '')  // Remove company suffixes
              .trim();
          };
          
          // Check if this intent item matches any branded keyword from Sheet 2
          brandedKeywords.forEach((label, brandedKeyword) => {
            const normalizedKeyword = normalizeForMatching(brandedKeyword);
            const normalizedIntentItem = normalizeForMatching(item);
            
            // Try exact match first (most reliable)
            if (normalizedIntentItem === normalizedKeyword) {
              foundMatches.push(`${item} -> ${brandedKeyword} (${label}) [EXACT]`);
              console.log(`🎯 EXACT MATCH FOUND: "${item}" matches "${brandedKeyword}" (${label})`);
              if (label === 'client') {
                hasClientKeyword = true;
              } else if (label === 'competitor') {
                hasCompetitorKeyword = true;
              }
            }
          });
        });
      });
      
      // Count per bucket once per company
      if (hasClientKeyword && !processedClientCompanies.has(normalizedCompanyName)) {
        clientBrandEngagement++;
        processedClientCompanies.add(normalizedCompanyName);
        console.log(`✅ Client Brand Match - Company: "${companyName}", Matches:`, foundMatches);
      }
      if (hasCompetitorKeyword && !processedCompetitorCompanies.has(normalizedCompanyName)) {
        competitorBrandEngagement++;
        processedCompetitorCompanies.add(normalizedCompanyName);
        console.log(`🔴 Competitor Brand Match - Company: "${companyName}", Matches:`, foundMatches);
      }
    });

    console.log('📊 Final Results:');
    console.log('🎯 Client Brand Engagement:', clientBrandEngagement);
    console.log('🔴 Competitor Brand Engagement:', competitorBrandEngagement);
    console.log('🏢 Processed Companies:', Array.from(new Set([
      ...Array.from(processedClientCompanies),
      ...Array.from(processedCompetitorCompanies)
    ])));
    console.log('🔍 BRAND ENGAGEMENT CALCULATION COMPLETED - Client:', clientBrandEngagement, 'Competitor:', competitorBrandEngagement);

    filteredAnalyticsData.forEach((row, index) => {
      
      // Count interested leads when Call Outcome is explicitly Connected – Interested
      const callOutcome = getFieldValue(row, ['Call Outcome', 'CallOutcome', 'Call Status', 'Outcome', 'callOutcome', 'call_outcome']);

      if (isConnectedInterested(callOutcome)) {
        totalInterestedLeads++;
      }

      // Box 5: Website Visitors - Count unique companies with Website Engagement
      const websiteEngagement = getFieldValue(row, COMMON_FIELD_COMBINATIONS.WEBSITE_ENGAGEMENT as unknown as string[]);
      const companyName = getFieldValue(row, ['Company Name', 'Company', 'Organization']);
      
      if (websiteEngagement && companyName) {
        // Check if there's any website engagement data (not empty, not N/A, not 0)
        const engagementValue = String(websiteEngagement).trim();
        // Accept ANY non-empty value: numbers (1000, 2, 1, 1, 1) OR URLs
        // Each company counts as exactly 1 visitor regardless of the actual number
        if (engagementValue && engagementValue !== 'N/A' && engagementValue !== '0' && engagementValue !== '') {
          // CRITICAL: Each company is counted only once regardless of visit numbers
          // Example: Company ABC with 100 leads showing "1000", "2", "1", "1", "1" = counts as 1 visitor
          // Example: Company XYZ with 50 leads showing URLs = counts as 1 visitor
          companiesWithWebsite.add(companyName);
        }
      }

      // Parse all time periods (excludes main Buying Intent column from analytics)
      const intent7Days = parseBuyingIntentForAnalytics(row['Last 7 Days'] || '');
      const intent30Days = parseBuyingIntentForAnalytics(row['Last 30 Days'] || '');
      const intent90Days = parseBuyingIntentForAnalytics(row['Last 90 Days'] || '');
      
      // Parse 7-day buying intent data

      // Collect unique topics for each time period (cumulative logic)
      const allTopicsForThisLead = new Set<string>();
      
      // Add 7-day topics (includes merged Data Intent data) - use frequency count from parentheses
      intent7Days.forEach(({ topic, count }) => {
        if (topic) {
          intent7DaysMap.set(topic, (intent7DaysMap.get(topic) || 0) + count);
          allTopicsForThisLead.add(topic);
          allIntentMap.set(topic, (allIntentMap.get(topic) || 0) + count);

          // Note: Brand engagement counting now handled by corrected logic above
        }
      });

      // Add 30-day topics - use frequency count from parentheses
      intent30Days.forEach(({ topic, count }) => {
        if (topic) {
          intent30DaysMap.set(topic, (intent30DaysMap.get(topic) || 0) + count);
          allTopicsForThisLead.add(topic);
          allIntentMap.set(topic, (allIntentMap.get(topic) || 0) + count);

          // Note: Brand engagement counting now handled by corrected logic above
        }
      });

      // Add 90-day topics - use frequency count from parentheses
      intent90Days.forEach(({ topic, count }) => {
        if (topic) {
          intent90DaysMap.set(topic, (intent90DaysMap.get(topic) || 0) + count);
          allTopicsForThisLead.add(topic);
          allIntentMap.set(topic, (allIntentMap.get(topic) || 0) + count);

          // Note: Brand engagement counting now handled by corrected logic above
        }
      });

      // Calculate individual average for this lead
      const uniqueTopicsCount = allTopicsForThisLead.size;
      
      if (uniqueTopicsCount > 0) {
        totalAverageSum += uniqueTopicsCount;
        validAverageCount++;
      }

      // Process industries for top industries
      const industry = getFieldValue(row, [
        'Company Industry', 'Industry', 'Business Industry',
        'Company Sector', 'Sector', 'Business Sector'
      ]);
      
      if (industry) {
        // Clean industry name - take only the part before "=" and clean it
        let cleanIndustry = String(industry);
        
        // If there's an "=" sign, take only the part before it
        if (cleanIndustry.includes('=')) {
          cleanIndustry = cleanIndustry.split('=')[0];
        }
        
        // Remove unwanted characters and clean up
        cleanIndustry = cleanIndustry
          .replace(/[{}\[\]"']/g, '') // Remove brackets, braces, quotes
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .trim();
        
        if (cleanIndustry && cleanIndustry.length > 0) {
          industryMap.set(cleanIndustry, (industryMap.get(cleanIndustry) || 0) + 1);
        }
      }

      // Process job titles for top positions
      const jobTitle = getFieldValue(row, [
        'Job Title', 'Position', 'Role', 'Job Function'
      ]);
      
      if (jobTitle) {
        // Clean job title - take only the part before "=" and clean it
        let cleanJobTitle = String(jobTitle);
        
        // If there's an "=" sign, take only the part before it
        if (cleanJobTitle.includes('=')) {
          cleanJobTitle = cleanJobTitle.split('=')[0];
        }
        
        // Remove unwanted characters and clean up
        cleanJobTitle = cleanJobTitle
          .replace(/[{}\[\]"']/g, '') // Remove brackets, braces, quotes
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .trim();
        
        if (cleanJobTitle && cleanJobTitle.length > 0) {
          jobTitleMap.set(cleanJobTitle, (jobTitleMap.get(cleanJobTitle) || 0) + 1);
        }
      }
    });

      // Calculate totals (these will now be logically consistent)
  const total7Days = Array.from(intent7DaysMap.values()).reduce((sum, count) => sum + count, 0);
  const total30Days = Array.from(intent30DaysMap.values()).reduce((sum, count) => sum + count, 0);
  const total90Days = Array.from(intent90DaysMap.values()).reduce((sum, count) => sum + count, 0);
  
  // Debug: Log the final totals
  

    // Find top intents
    const getTopIntentFromMap = (intentMap: Map<string, number>, label: string): string => {
      if (intentMap.size === 0) return 'No Data';
      const sortedTopics = Array.from(intentMap.entries())
        .sort((a, b) => b[1] - a[1]);
      return `${label}: ${sortedTopics.length > 0 ? sortedTopics[0][0] : 'N/A'}`;
    };

    // Get top industries (top 1)
    const topIndustries = Array.from(industryMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 1)
      .map(([industry]) => industry);

    // Get top job positions (top 1)
    const topJobPositions = Array.from(jobTitleMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 1)
      .map(([position]) => position);

    // Get primary interests (top 3 overall)
    const sortedAllIntents = Array.from(allIntentMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    
    const primaryInterests = sortedAllIntents.length > 0 
      ? sortedAllIntents.map(([topic]) => topic)
      : ['No Data'];

    const overallAverageBuyingIntent = validAverageCount > 0 
      ? totalAverageSum / validAverageCount 
      : 0;

    // Count unique companies with website URLs
    const websiteVisitors = companiesWithWebsite.size;

    return {
      totalLeads,
      totalInterestedLeads,
      totalGenericBuyingIntents,
      totalBrandedBuyingIntents,
      timesClientBrandSearched: clientBrandEngagement, // Updated with corrected logic
      competitorBrands: competitorBrandEngagement,     // Updated with corrected logic
      websiteVisitors,
      topIndustries,
      topJobPositions,
      intent7DaysCount: total7Days,
      intent30DaysCount: total30Days,
      intent90DaysCount: total90Days,
      topIntent7Days: getTopIntentFromMap(intent7DaysMap, 'Most Searched (7d)'),
      topIntent30Days: getTopIntentFromMap(intent30DaysMap, 'Most Searched (30d)'),
      topIntent90Days: getTopIntentFromMap(intent90DaysMap, 'Most Searched (90d)'),
      totalBuyingIntent7Days: total7Days,
      totalBuyingIntent30Days: total30Days,
      totalBuyingIntent90Days: total90Days,
      overallAverageBuyingIntent: parseFloat(overallAverageBuyingIntent.toFixed(2)),
      primaryInterests
    };
  }, [filteredAnalyticsData, buyingIntentData, user?.companyName, uploadedData]);

  console.log('✅ Brand Engagement Calculation COMPLETED in DashboardViewer');

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

        useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Check for temporary access token
        const tempToken = sessionStorage.getItem('temp_client_token');
        const storedUsername = sessionStorage.getItem('temp_client_username');
        let token = sessionStorage.getItem('token'); // Changed from localStorage
        let isTemp = false;

        // If there's a temporary token and the username matches, use it
        if (tempToken && storedUsername === username) {
          token = tempToken;
          isTemp = true;
        }

        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await api.get('/api/client/dashboard');

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to fetch dashboard data');
        }

        // Verify username matches the response data
        if (username && response.data.data.username !== username) {
          throw new Error('Invalid client access');
        }

        setIsTemporaryAccess(isTemp);
        setDashboardData(response.data.data);
        
        // Trigger animations after data loads
        setTimeout(() => {
          setAnimationLoaded(true);
        }, 100);
      } catch (error: any) {
        console.error('Error fetching dashboard data:', error);
        
        // Check if account is deactivated
        if (error.response?.status === 401) {
          if (error.response?.data?.accountDeactivated) {
            // Account has been deactivated, redirect to deactivated page
            sessionStorage.removeItem('token'); // Changed from localStorage
            sessionStorage.removeItem('temp_client_token');
            sessionStorage.removeItem('temp_client_username');
            window.location.href = '/account-deactivated';
            return;
          } else {
            // Session expired, redirect to login
            sessionStorage.removeItem('token'); // Changed from localStorage
            
            // Determine redirect based on company context
            const currentPath = window.location.pathname;
            const pathParts = currentPath.split('/');
            const companySlug = pathParts[1];
            
            let redirectTo;
            // Check if we have a saved login URL for this company
            const savedLoginUrl = sessionStorage.getItem('lastLoginUrl');
            if (savedLoginUrl && companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug)) {
              // Extract company slug from saved URL and redirect directly to company login
              const savedPathParts = savedLoginUrl.split('/');
              const savedCompanySlug = savedPathParts[1];
              if (savedCompanySlug === companySlug) {
                redirectTo = `/${companySlug}/login`;
              } else {
                redirectTo = `/${companySlug}/login`;
              }
            } else if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug)) {
              redirectTo = `/${companySlug}/login`;
            } else {
              redirectTo = '/client-signup';
            }
            
            window.location.href = redirectTo;
            return;
          }
        }
        
        setError(error.response?.data?.message || error.message || 'Failed to fetch dashboard data');
        
        // Clear temporary access tokens if there's an error
        if (sessionStorage.getItem('temp_client_token')) {
          sessionStorage.removeItem('temp_client_token');
          sessionStorage.removeItem('temp_client_username');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [username, user]);

  useEffect(() => {
    const fetchUploadedData = async () => {
      try {
        setDataLoading(true);
        
        // Check for temporary access token
        const tempToken = sessionStorage.getItem('temp_client_token');
        const storedUsername = sessionStorage.getItem('temp_client_username');
        let token = sessionStorage.getItem('token'); // Changed from localStorage

        // If there's a temporary token and the username matches, use it
        if (tempToken && storedUsername === username) {
          token = tempToken;
        }

        if (!token) {
          return;
        }

        const response = await api.get('/api/upload/client/data?sheet=1');

        if (response.data.success) {
          const data = response.data.data;
          console.log('🔍 DEBUG: Frontend received data:', data);
          console.log('🔍 DEBUG: Number of sheets:', data.sheets?.length);
          console.log('🔍 DEBUG: Sheet details:', data.sheets?.map((s: SheetData) => ({ 
            fileName: s.fileName, 
            originalFileName: s.originalFileName, 
            fileType: s.fileType,
            recordCount: s.recordCount
          })));
          setUploadedData(data);
        }
      } catch (error: any) {
        console.error('Error fetching uploaded data:', error);
        
        // Check if account is deactivated
        if (error.response?.status === 401) {
          if (error.response?.data?.accountDeactivated) {
            // Account has been deactivated, redirect to deactivated page
            sessionStorage.removeItem('token'); // Changed from localStorage
            sessionStorage.removeItem('temp_client_token');
            sessionStorage.removeItem('temp_client_username');
            window.location.href = '/account-deactivated';
            return;
          }
        }
        
        setUploadedData({ hasData: false, sheets: [], message: 'No data available' });
      } finally {
        setDataLoading(false);
      }
    };

    if (dashboardData) {
      fetchUploadedData();
      fetchBuyingIntentData();
    }
  }, [dashboardData, username]);

  // Silent background refresh with user activity detection
  useEffect(() => {
    let lastUserActivity = Date.now();
    let intervalId: NodeJS.Timeout;

    // Track user activity to avoid interrupting interactions
    const updateActivity = () => {
      lastUserActivity = Date.now();
    };

    // Add activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    const silentRefresh = async () => {
      // Only refresh if user hasn't been active for 5 seconds
      const timeSinceActivity = Date.now() - lastUserActivity;
      if (timeSinceActivity < 5000 || !dashboardData) {
        return;
      }

      try {
        // Silent fetch without loading states - let axios interceptor handle auth

        // Fetch both data sources silently - let api interceptor handle auth
        const [uploadResponse, buyingIntentResponse] = await Promise.allSettled([
          api.get('/api/upload/client/data?sheet=1'), // FIX: Only fetch Sheet 1
          api.get('/api/upload/client/buying-intent')
        ]);

        // Update data silently (no loading states)
        if (uploadResponse.status === 'fulfilled' && uploadResponse.value.data.success) {
          setUploadedData(uploadResponse.value.data.data);
        } else if (uploadResponse.status === 'fulfilled') {
          setUploadedData({ hasData: false, sheets: [], message: 'No data available' });
        }

        if (buyingIntentResponse.status === 'fulfilled' && buyingIntentResponse.value.data.success) {
          setBuyingIntentData(buyingIntentResponse.value.data.data);
        } else if (buyingIntentResponse.status === 'fulfilled') {
          setBuyingIntentData({ hasData: false, buyingIntents: [], message: 'No data available' });
        }
      } catch (error) {
        // Silent error handling - don't disrupt user experience
      }
    };

    // Check every 15 seconds (less intrusive) but only refresh when user is idle
    intervalId = setInterval(silentRefresh, 15000);

    return () => {
      clearInterval(intervalId);
      // Remove activity listeners
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [dashboardData, username]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Clean and split keyword into individual keywords
  const cleanAndSplitKeywords = (keyword: string): string[] => {
    if (!keyword || typeof keyword !== 'string') return [];
    
    // First, clean the keyword
    let cleanedKeyword = keyword
      // Remove common unwanted characters and symbols (but keep commas and semicolons for splitting)
      .replace(/[{}[\]()<>"'`~!@#$%^&*+=|\\:;?/]/g, '')
      // Remove extra whitespace and normalize
      .replace(/\s+/g, ' ')
      // Remove leading/trailing whitespace
      .trim();
    
    // Split only by commas and semicolons (not by spaces to preserve logical phrases)
    const splitKeywords = cleanedKeyword
      .split(/[,;]+/) // Split only by commas and semicolons
      .map(k => k.trim())
      .filter(k => k.length > 0); // Keep ALL keywords, even 1-2 character ones
    
    // Clean each individual keyword - MINIMAL CLEANING to preserve all intent data
    const cleanedKeywords = splitKeywords.map(k => {
      return k
        // Remove leading/trailing commas and other unwanted characters
        .replace(/^[,.\s]+/, '') // Remove leading commas, dots, spaces
        .replace(/[,.\s]+$/, '') // Remove trailing commas, dots, spaces
        // Normalize whitespace
        .replace(/\s+/g, ' ')
        .trim();
    })
    .filter(k => k.length > 0); // Keep ALL keywords, even 1 character
    
    return cleanedKeywords;
  };

  // Clean keyword by removing unwanted characters and formatting (for backward compatibility)
  const cleanKeyword = (keyword: string): string => {
    if (!keyword || typeof keyword !== 'string') return '';
    
    return keyword
      // Remove common unwanted characters and symbols
      .replace(/[{}[\]()<>"'`~!@#$%^&*+=|\\:;?/.,]/g, '')
      // Remove extra whitespace and normalize
      .replace(/\s+/g, ' ')
      // Remove leading/trailing whitespace
      .trim()
      // Remove common unwanted words/phrases
      .replace(/\b(Industry|Sector|Category|Type|Business|Company|Corporate|Enterprise)\b/gi, '')
      .replace(/\b(Management|Administration|Operations|Systems|Solutions|Technology|Tech)\b/gi, '')
      .replace(/\b(International|Global|Worldwide|National|Regional|Local)\b/gi, '')
      .replace(/\b(Inc|LLC|Ltd|Corp|Corporation|Company|Co|Group|Associates|Partners)\b/gi, '')
      // Remove numbers that are likely not part of the keyword
      .replace(/\b\d{1,2}\b/g, '')
      // Remove single characters that are likely not meaningful
      .replace(/\b[a-zA-Z]\b/g, '')
      // Clean up any remaining extra whitespace
      .replace(/\s+/g, ' ')
      .trim();
  };

  // 9 Color variations cycling system (20% opacity background, solid text)
  const pillColors = [
    { bg: '#FF6D8D', text: '#FF6D8D', opacity: 0.2 }, // Button 1: Red
    { bg: '#60C98C', text: '#60C98C', opacity: 0.2 }, // Button 2: Green
    { bg: '#E6C717', text: '#E6C717', opacity: 0.2 }, // Button 3: Yellow
    { bg: '#942FED', text: '#942FED', opacity: 0.2 }, // Button 4: Purple
    { bg: '#43BBEF', text: '#43BBEF', opacity: 0.2 }, // Button 5: Blue
    { bg: '#E61717', text: '#E61717', opacity: 0.2 }, // Button 6: Red
    { bg: '#FFC400', text: '#FFC400', opacity: 0.2 }, // Button 7: Yellow
    { bg: '#FF7700', text: '#FF7700', opacity: 0.2 }, // Button 8: Orange
    { bg: '#1760E6', text: '#1760E6', opacity: 0.2 }  // Button 9: Blue
  ];

  // Generate pill color based on order (cycles through colors 1-9 in order)
  const getPillColor = (index: number) => {
    // Use modulo to cycle through colors in order: 0, 1, 2, 3, 4, 5, 6, 7, 8, 0, 1, 2...
    const colorIndex = index % pillColors.length;
    const color = pillColors[colorIndex];
    
    // Ensure we always have a visible background color
    const backgroundColor = color.bg || '#424242'; // Fallback to dark gray if undefined
    const textColor = color.text || '#FFFFFF'; // Fallback to white if undefined
    const opacity = color.opacity || 0.2; // Use specified opacity or default to 0.2
    
    // Convert hex to rgba for opacity support
    const hexToRgba = (hex: string, opacity: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };
    
    return {
      backgroundColor: hexToRgba(backgroundColor, opacity),
      color: backgroundColor, // Use the solid background color for text (not the rgba version)
      border: `1px solid ${backgroundColor}`,
      borderRadius: '16px', // Pill shape with rounded corners
    };
  };

  // Handle company card click
  const handleCompanyClick = (company: any) => {
    setSelectedCompany(company);
    setPopupOpen(true);
  };

  // Close popup
  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedCompany(null);
    setCompanyLeadsFilter([]);
    setExpandedLeadRow(null);
  };

  // Handle expand/collapse lead row
  const handleExpandLeadRow = (index: number) => {
    setExpandedLeadRow(expandedLeadRow === index ? null : index);
  };

  // Handle column resizing
  const handleColumnResize = (
    columnId: string, 
    width: number, 
    isPopupTable: boolean = false
  ) => {
    if (isPopupTable) {
      setPopupTableColumnWidths(prev => ({
        ...prev,
        [columnId]: Math.max(width, 100) // Minimum width of 100px
      }));
    } else {
      setMainTableColumnWidths(prev => ({
        ...prev,
        [columnId]: Math.max(width, 100) // Minimum width of 100px
      }));
    }
  };

  // Handle detail table column resizing
  const handleDetailColumnResize = (columnId: string, width: number) => {
    setDetailTableColumnWidths(prev => ({
      ...prev,
      [columnId]: Math.max(width, 100) // Minimum width of 100px
    }));
  };

  // Get column width
  const getColumnWidth = (columnId: string, isPopupTable: boolean = false) => {
    const widths = isPopupTable ? popupTableColumnWidths : mainTableColumnWidths;
    return widths[columnId] || 180; // Default width
  };

  // Get detail table column width
  const getDetailColumnWidth = (columnId: string): number => {
    return detailTableColumnWidths[columnId] || 200; // Default width
  };

  // Auto-fit column to content width (optimized for performance)
  const autoFitColumn = (columnId: string, isPopupTable: boolean = false) => {
    const columnKey = `${columnId}-${isPopupTable ? 'popup' : 'main'}`;
    const isCurrentlyFitted = autoFittedColumns[columnKey];
    
    if (isCurrentlyFitted) {
      // Collapse back to default width
      requestAnimationFrame(() => {
        handleColumnResize(columnId, 180, isPopupTable);
        setAutoFittedColumns(prev => ({
          ...prev,
          [columnKey]: false
        }));
      });
    } else {
      // Calculate content width by finding the longest content in the column
      requestAnimationFrame(() => {
        let maxWidth = 100; // Minimum width
        const headerLength = columnId.length * 10 + 40; // Approximate header width
        maxWidth = Math.max(maxWidth, headerLength);
        
        // Get visible data rows - only sample first 100 rows for performance
        const data = isPopupTable ? selectedCompany?.allData : allData;
        if (data && Array.isArray(data)) {
          const sampleSize = Math.min(100, data.length); // Only check first 100 rows
          for (let i = 0; i < sampleSize; i++) {
            const row = data[i];
            const cellValue = String(row[columnId] || '');
            const contentWidth = cellValue.length * 8 + 30; // Approximate content width
            maxWidth = Math.max(maxWidth, contentWidth);
          }
        }
        
        // Cap maximum width at 500px for usability
        maxWidth = Math.min(maxWidth, 500);
        
        handleColumnResize(columnId, maxWidth, isPopupTable);
        setAutoFittedColumns(prev => ({
          ...prev,
          [columnKey]: true
        }));
      });
    }
  };

  // Resizable column header component
  const ResizableTableCell = ({ 
    children, 
    columnId, 
    isPopupTable = false,
    sx = {},
    ...props 
  }: any) => {
    const [isResizing, setIsResizing] = useState(false);

    const handleDoubleClick = () => {
      autoFitColumn(columnId, isPopupTable);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const startX = e.clientX;
      const startWidth = getColumnWidth(columnId, isPopupTable);
      setIsResizing(true);

      const handleMouseMove = (e: MouseEvent) => {
        const diff = e.clientX - startX;
        const newWidth = Math.max(startWidth + diff, 100);
        handleColumnResize(columnId, newWidth, isPopupTable);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };

    return (
      <TableCell
        {...props}
        onDoubleClick={handleDoubleClick}
        sx={{
          ...sx,
          width: getColumnWidth(columnId, isPopupTable),
          minWidth: getColumnWidth(columnId, isPopupTable),
          maxWidth: getColumnWidth(columnId, isPopupTable),
          position: 'relative',
          userSelect: isResizing ? 'none' : 'auto',
          overflow: 'visible',
          cursor: 'default',
          transition: 'width 0.2s ease-out, min-width 0.2s ease-out, max-width 0.2s ease-out',
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
          }
        }}
        title="Double-click to auto-fit column width"
      >
        {children}
        <Box
          sx={{
            position: 'absolute',
            right: -2,
            top: 0,
            bottom: 0,
            width: '8px',
            cursor: 'col-resize',
            backgroundColor: isResizing ? theme.palette.primary.main : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor: theme.palette.primary.main + '66',
            },
            zIndex: 10,
            borderRadius: '0 2px 2px 0'
          }}
          onMouseDown={handleMouseDown}
        />
      </TableCell>
    );
  };

  // Resizable detail table cell component for Field/Value table
  const ResizableDetailTableCell = ({ 
    children, 
    columnId, 
    sx = {},
    ...props 
  }: any) => {
    const [isResizing, setIsResizing] = useState(false);

    const handleDoubleClick = () => {
      // Auto-fit to content
      const element = document.querySelector(`[data-column-id="${columnId}"]`);
      if (element) {
        const contentWidth = element.scrollWidth;
        handleDetailColumnResize(columnId, Math.max(contentWidth + 20, 200));
      }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const startX = e.clientX;
      const startWidth = getDetailColumnWidth(columnId);
      setIsResizing(true);

      const handleMouseMove = (e: MouseEvent) => {
        const diff = e.clientX - startX;
        const newWidth = Math.max(startWidth + diff, 100);
        handleDetailColumnResize(columnId, newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };

    return (
      <TableCell
        {...props}
        data-column-id={columnId}
        sx={{
          ...sx,
          width: getDetailColumnWidth(columnId),
          minWidth: getDetailColumnWidth(columnId),
          maxWidth: getDetailColumnWidth(columnId),
          position: 'relative',
          borderRight: `1px solid ${theme.palette.divider}`,
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            backgroundColor: 'transparent',
            cursor: 'col-resize',
            zIndex: 1,
          },
          '&:hover::after': {
            backgroundColor: theme.palette.primary.main,
            opacity: 0.3,
          }
        }}
        onDoubleClick={handleDoubleClick}
        onMouseDown={handleMouseDown}
      >
        {children}
      </TableCell>
    );
  };

  // Duplicate function removed - using the enhanced version above

  // Clean column header names for better display
  const cleanColumnHeader = (columnName: string): string => {
    if (columnName === '_completedBy') return 'Completed By';
    return columnName;
  };

  // Ensure essential columns are always included
  const getColumnsWithEssentials = (columns: string[], allData: any[]) => {
    const essentialColumns = ['_completedBy'];
    const result = [...columns];
    
    // Add essential columns if they exist in the data but aren't in the selected columns
    essentialColumns.forEach(col => {
      if (!result.includes(col) && allData.some(row => row.hasOwnProperty(col))) {
        result.push(col);
      }
    });
    
    return result;
  };

  // Get useful columns for leads filtering
  const getUsefulColumns = (leads: any[]) => {
    if (!leads || leads.length === 0) return [];
    
    const allColumns = Object.keys(leads[0] || {});
    const editorColumns = ['Contact Status', 'Call Outcome', 'Lead Qualification'];
    const trackingColumns = ['_completedBy']; // User tracking columns
    
    const usefulColumns = allColumns.filter(col => {
      const columnLower = col.toLowerCase();
      return (
        columnLower.includes('name') ||
        columnLower.includes('email') ||
        columnLower.includes('phone') ||
        columnLower.includes('company') ||
        columnLower.includes('industry') ||
        columnLower.includes('title') ||
        columnLower.includes('position') ||
        columnLower.includes('country') ||
        columnLower.includes('location') ||
        columnLower.includes('buying intent') ||
        columnLower.includes('revenue') ||
        columnLower.includes('size') ||
        columnLower.includes('employees') ||
        columnLower.includes('website') ||
        columnLower.includes('linkedin') ||
        columnLower.includes('address') ||
        columnLower.includes('city') ||
        columnLower.includes('state') ||
        columnLower.includes('zip') ||
        columnLower.includes('technology') ||
        editorColumns.includes(col) || // Include editor workflow columns
        trackingColumns.includes(col) // Include user tracking columns
      );
    });
    
    return usefulColumns.length > 0 ? usefulColumns : allColumns.slice(0, 10);
  };

  // Initialize filter state for a country if it doesn't exist
  const initializeCountryFilter = (country: string) => {
    if (!countryFilters[country]) {
      setCountryFilters(prev => ({
        ...prev,
        [country]: {
          searchQuery: '',
          selectedBuyingIntents: [],
          selectedIndustries: [],
          selectedBranding: [],
          timeRange: 'last7days' // Default to 7 days
        }
      }));
    }
  };

  // Update search query for a specific country
  const updateCountrySearch = (country: string, searchQuery: string) => {
    initializeCountryFilter(country);
    setCountryFilters(prev => ({
      ...prev,
      [country]: {
        ...prev[country],
        searchQuery
      }
    }));
  };

  // Update filter selections for a specific country
  const updateCountryFilter = (country: string, filterType: 'buyingIntents' | 'industries' | 'branding', value: string, checked: boolean) => {
    initializeCountryFilter(country);
    setCountryFilters(prev => {
      const currentFilters = prev[country] || { searchQuery: '', selectedBuyingIntents: [], selectedIndustries: [], selectedBranding: [], timeRange: 'last7days' };
      const currentSelection = filterType === 'buyingIntents' ? currentFilters.selectedBuyingIntents : 
                              filterType === 'industries' ? currentFilters.selectedIndustries : 
                              currentFilters.selectedBranding;
      
      let newSelection: string[];
      if (checked) {
        newSelection = [...currentSelection, value];
      } else {
        newSelection = currentSelection.filter(item => item !== value);
      }

      return {
        ...prev,
        [country]: {
          ...currentFilters,
          [filterType === 'buyingIntents' ? 'selectedBuyingIntents' : 
           filterType === 'industries' ? 'selectedIndustries' : 
           'selectedBranding']: newSelection
        }
      };
    });
  };

  // Clear all filters for a specific country
  const clearCountryFilters = (country: string) => {
    setCountryFilters(prev => ({
      ...prev,
      [country]: {
        searchQuery: '',
        selectedBuyingIntents: [],
        selectedIndustries: [],
        selectedBranding: [],
        timeRange: 'last7days' // Reset to default
      }
    }));
  };

  // Update time range for a specific country
  const updateCountryTimeRange = (country: string, timeRange: 'last7days' | 'last30days' | 'last90days') => {
    initializeCountryFilter(country);
    setCountryFilters(prev => ({
      ...prev,
      [country]: {
        ...prev[country],
        timeRange: timeRange
      }
    }));
  };

  // Filter companies for a specific country based on search and filters
  const filterCompaniesForCountry = (companies: CompanyData[], country: string) => {
    const filters = countryFilters[country];
    if (!filters) return companies;

    return companies.filter(company => {
      // Search filter - require minimum 3 characters
      if (filters.searchQuery && filters.searchQuery.length >= 3) {
        const searchMatch = 
          company.name.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
          Array.from(company.allKeywords as Set<string>).some(keyword => 
            keyword.toLowerCase().includes(filters.searchQuery.toLowerCase())
          ) ||
          Array.from(company.industries as Set<string>).some(industry => 
            industry.toLowerCase().includes(filters.searchQuery.toLowerCase())
          );
        if (!searchMatch) return false;
      }

      // Buying intent filter
    if (filters.selectedBuyingIntents.length > 0) {
      const normalizedSelectedIntents = new Set(
        filters.selectedBuyingIntents.map(intent => normalizeKeywordForMatch(String(intent)))
      );
      const hasMatchingBuyingIntent = Array.from(company.keywords as Set<string>).some(keyword => {
        const normalizedKeyword = normalizeKeywordForMatch(String(keyword));
        return normalizedKeyword.length > 0 && normalizedSelectedIntents.has(normalizedKeyword);
      });
        if (!hasMatchingBuyingIntent) return false;
      }

      // Industry filter
      if (filters.selectedIndustries.length > 0) {
        const hasMatchingIndustry = filters.selectedIndustries.some(industry =>
          Array.from(company.industries as Set<string>).some(companyIndustry => 
            companyIndustry.toLowerCase().includes(industry.toLowerCase())
          )
        );
        if (!hasMatchingIndustry) return false;
      }

      // Branding filter - check for branded keywords in the Branded Buying Intent zone
      if (filters.selectedBranding.length > 0) {
        const hasMatchingBranding = filters.selectedBranding.some(selectedBranding => {
          // Check if this company has this branded keyword in buyingIntentData
          if (!buyingIntentData?.hasData || company.allKeywords.size === 0) return false;
          
          // Get all Sheet 1 keywords (Generic Buying Intent) for this company
          const sheet1Keywords = new Set(
            Array.from(company.allKeywords).map(k => normalizeKeywordForMatch(String(k)))
          );
          
          // Check if the selected branding keyword exists in buyingIntentData as "Branded" 
          // and matches any Sheet 1 keyword for this company
          let hasBrandedKeyword = false;
          buyingIntentData.buyingIntents.forEach((intent: any) => {
            const keyword = (intent.keyword || '').trim();
            const category = intent.category || '';
            
            // Only check if category is "Branded"
            if (category === 'Branded' && keyword) {
              const normalizedKeyword = normalizeKeywordForMatch(keyword);
              const normalizedSelected = normalizeKeywordForMatch(selectedBranding);
              
              // Check if this branded keyword matches the selected branding filter
              if (normalizedKeyword && normalizedKeyword === normalizedSelected) {
                // Check if this branded keyword also matches any of the company's Sheet 1 keywords
                if (sheet1Keywords.has(normalizedKeyword)) {
                  hasBrandedKeyword = true;
                }
              }
            }
          });
          
          return hasBrandedKeyword;
        });
        if (!hasMatchingBranding) return false;
      }

      return true;
    });
  };

  // Get filtered keywords for display (only show matching ones)
  const getFilteredKeywords = (company: CompanyData, country: string) => {
    const filters = countryFilters[country];
    if (!filters) {
      // Even without filters, still need to exclude branded keywords
      let keywords = Array.from(company.keywords as Set<string>);
      
      // Filter out keywords that are marked as "Branded" in Sheet 2
      if (buyingIntentData?.hasData) {
    const brandedKeywordsSet = new Set<string>();
    const sheet1Keywords = new Set(
      keywords.map(k => normalizeKeywordForMatch(String(k)))
    );
        
        buyingIntentData.buyingIntents.forEach((intent: any) => {
          const keyword = (intent.keyword || '').trim();
          const category = intent.category || '';
          
      if (category === 'Branded' && keyword) {
        const keywordNormalized = normalizeKeywordForMatch(keyword);
        
        // Check if this branded keyword matches any Sheet 1 keyword
        if (keywordNormalized && sheet1Keywords.has(keywordNormalized)) {
          const originalKeyword = keywords.find(
            k => normalizeKeywordForMatch(String(k)) === keywordNormalized
          );
              if (originalKeyword) {
            brandedKeywordsSet.add(normalizeKeywordForMatch(String(originalKeyword)));
              }
            }
          }
        });
        
        // Remove branded keywords from generic list
        keywords = keywords.filter(
      keyword => !brandedKeywordsSet.has(normalizeKeywordForMatch(String(keyword)))
        );
      }
      
      return keywords;
    }
    
    let keywords = Array.from(company.keywords as Set<string>);
    
    // Filter out keywords that are marked as "Branded" in Sheet 2
    if (buyingIntentData?.hasData) {
      const brandedKeywordsSet = new Set<string>();
      const sheet1Keywords = new Set(
        keywords.map(k => normalizeKeywordForMatch(String(k)))
      );
      
      buyingIntentData.buyingIntents.forEach((intent: any) => {
        const keyword = (intent.keyword || '').trim();
        const category = intent.category || '';
        
        if (category === 'Branded' && keyword) {
          const keywordNormalized = normalizeKeywordForMatch(keyword);
          
          // Check if this branded keyword matches any Sheet 1 keyword
          if (keywordNormalized && sheet1Keywords.has(keywordNormalized)) {
            const originalKeyword = keywords.find(
              k => normalizeKeywordForMatch(String(k)) === keywordNormalized
            );
            if (originalKeyword) {
              brandedKeywordsSet.add(normalizeKeywordForMatch(String(originalKeyword)));
            }
          }
        }
      });
      
      // Remove branded keywords from generic list
      keywords = keywords.filter(
        keyword => !brandedKeywordsSet.has(normalizeKeywordForMatch(String(keyword)))
      );
    }
    
    // If search query exists and is 3+ characters
    if (filters.searchQuery && filters.searchQuery.length >= 3) {
      // Check if company name matches the search
      const companyNameMatches = company.name.toLowerCase().includes(filters.searchQuery.toLowerCase());
      
      // Only filter keywords if company name doesn't match
      // (if company name matches, show all keywords)
      if (!companyNameMatches) {
        const normalizedQuery = filters.searchQuery.toLowerCase().trim();
        keywords = keywords.filter(keyword =>
          keyword.toLowerCase().trim().includes(normalizedQuery)
        );
      }
    }
    
    // If buying intent filters are applied, only show matching keywords
    if (filters.selectedBuyingIntents.length > 0) {
      const normalizedSelected = filters.selectedBuyingIntents.map(intent =>
        normalizeKeywordForMatch(String(intent))
      );
      keywords = keywords.filter(keyword => {
        const normalizedKeyword = normalizeKeywordForMatch(String(keyword));
        return normalizedKeyword.length > 0 && normalizedSelected.some(intent => intent === normalizedKeyword);
      });
    }
    
    return keywords;
  };

  // Get filtered industries for display (only show matching ones)
  const getFilteredIndustries = (company: CompanyData, country: string) => {
    const filters = countryFilters[country];
    if (!filters) return Array.from(company.industries as Set<string>);
    
    let industries = Array.from(company.industries as Set<string>);
    
    // If search query exists and is 3+ characters
    if (filters.searchQuery && filters.searchQuery.length >= 3) {
      // Check if company name matches the search
      const companyNameMatches = company.name.toLowerCase().includes(filters.searchQuery.toLowerCase());
      
      // Only filter industries if company name doesn't match
      // (if company name matches, show all industries)
      if (!companyNameMatches) {
        industries = industries.filter(industry => 
          industry.toLowerCase().includes(filters.searchQuery.toLowerCase())
        );
      }
    }
    
    // If industry filters are applied, only show matching industries
    if (filters.selectedIndustries.length > 0) {
      industries = industries.filter(industry =>
        filters.selectedIndustries.some(selectedIndustry =>
          industry.toLowerCase().includes(selectedIndustry.toLowerCase())
        )
      );
    }
    
    return industries;
  };

  // Get unique buying intents for a country with smart deduplication
  const getBuyingIntentsForCountry = (companies: CompanyData[]) => {
    const intentsMap = new Map<string, string>();
    
    // Get all branded keywords from Sheet 2 to exclude them
    const brandedKeywordsSet = new Set<string>();
    // Get all Sheet 2 Generic keywords to enforce intersection
    const sheet2GenericSet = new Set<string>();
    if (buyingIntentData?.hasData) {
      buyingIntentData.buyingIntents.forEach((intent: any) => {
        const keyword = (intent.keyword || '').trim();
        const category = intent.category || '';
        if (category === 'Branded' && keyword) {
          const normalized = normalizeKeywordForMatch(keyword);
          if (normalized) {
            brandedKeywordsSet.add(normalized);
          }
        }
        if (category === 'Generic' && keyword) {
          const normalized = normalizeKeywordForMatch(keyword);
          if (normalized) {
            sheet2GenericSet.add(normalized);
          }
        }
      });
    }
    
    const normalizeKeyword = (keyword: string): string => {
      let normalized = normalizeKeywordForMatch(keyword);
      if (normalized.endsWith('s') && !normalized.endsWith('ss')) {
        normalized = normalized.slice(0, -1);
      }
      normalized = normalized.replace(/\s+insurance$/i, '');
      return normalized;
    };
    
    companies.forEach(company => {
      Array.from(company.keywords as Set<string>).forEach(keyword => {
        const keywordNormalizedForMatch = normalizeKeywordForMatch(String(keyword));
        
        // Exclude branded
        if (keywordNormalizedForMatch && brandedKeywordsSet.has(keywordNormalizedForMatch)) {
          return;
        }
        
        // NOTE: Removed Sheet 2 Generic intersection enforcement to show all keywords from uploaded data
        // This ensures all keywords from Sheet 1 appear in the filter, not just those in Sheet 2
        
        const normalizedKey = normalizeKeyword(keyword);
        if (!intentsMap.has(normalizedKey)) {
          intentsMap.set(normalizedKey, keyword);
        } else {
          const existing = intentsMap.get(normalizedKey)!;
          if (keyword.length < existing.length) {
            intentsMap.set(normalizedKey, keyword);
          }
        }
      });
    });
    
    return Array.from(intentsMap.values()).sort();
  };

  // Get unique industries for a country
  const getIndustriesForCountry = (companies: CompanyData[]) => {
    const industries = new Set<string>();
    companies.forEach(company => {
      Array.from(company.industries as Set<string>).forEach(industry => industries.add(industry));
    });
    return Array.from(industries).sort();
  };

  // Get unique branding options for a country (only from Branded category in buyingIntentData that match company keywords)
  const getBrandingForCountry = (companies: CompanyData[]) => {
    const brandingMap = new Map<string, string>(); // lowercase key -> original keyword
    
    // Only get branded keywords from buyingIntentData where category is "Branded" 
    // AND that match at least one company's buying intent keywords (including branded ones)
    if (buyingIntentData?.hasData && companies.length > 0) {
      // Collect all keywords from all companies (use allKeywords to include branded keywords from Sheet 1)
      const allCompanyKeywords = new Set<string>();
      companies.forEach(company => {
        Array.from(company.allKeywords).forEach(keyword => {
          const normalized = normalizeKeywordForMatch(String(keyword));
          if (normalized) {
            allCompanyKeywords.add(normalized);
          }
        });
      });
      
      // Filter branded keywords that match any company keyword
      buyingIntentData.buyingIntents.forEach((intent: any) => {
        const keyword = (intent.keyword || '').trim();
        const category = intent.category || '';
        
        // Only include if category is "Branded"
        if (category === 'Branded' && keyword) {
          const normalizedKeyword = normalizeKeywordForMatch(keyword);
          
          if (!normalizedKeyword) return;
          
          // Check if this branded keyword matches any company's keyword (including branded ones from Sheet 1)
          const matchesAnyCompany = allCompanyKeywords.has(normalizedKeyword);
          
          if (matchesAnyCompany) {
            // Use Map to deduplicate case variations (AON vs Aon)
            if (!brandingMap.has(normalizedKeyword)) {
              brandingMap.set(normalizedKeyword, keyword);
            } else {
              // If duplicate, prefer the one with more uppercase letters (likely proper case)
              const existing = brandingMap.get(normalizedKeyword)!;
              const existingUpperCount = (existing.match(/[A-Z]/g) || []).length;
              const newUpperCount = (keyword.match(/[A-Z]/g) || []).length;
              if (newUpperCount > existingUpperCount) {
                brandingMap.set(normalizedKeyword, keyword);
              }
            }
          }
        }
      });
    }
    
    return Array.from(brandingMap.values()).sort();
  };

  // Country sorting functions
  const toggleSortType = () => {
    setCountrySortType(current => current === 'alphabetical' ? 'numerical' : 'alphabetical');
  };

  const toggleNumericalOrder = () => {
    setNumericalSortOrder(current => current === 'desc' ? 'asc' : 'desc');
  };

  const getSortTypeIcon = () => {
    return countrySortType === 'alphabetical' ? 'A-Z' : '#';
  };

  const getNumericalSortIcon = () => {
    return numericalSortOrder === 'desc' ? '↓' : '↑';
  };

  const getSortTypeTooltip = () => {
    return countrySortType === 'alphabetical' 
      ? 'Switch to numerical sorting (by company count)' 
      : 'Switch to alphabetical sorting (A-Z)';
  };

  const getNumericalSortTooltip = () => {
    return numericalSortOrder === 'desc' 
      ? 'Sorted by company count (most first)' 
      : 'Sorted by company count (least first)';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        p: 0, 
        backgroundColor: theme.palette.mode === 'dark' ? '#191919' : '#F5F6FA',
        minHeight: '100vh'
      }} className={theme.palette.mode === 'dark' ? 'dashboard-dark-mode' : 'dashboard-light-mode'}>
        <Container>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 0, 
      backgroundColor: theme.palette.mode === 'dark' ? '#081028' : '#F5F6FA',
      minHeight: '100vh'
    }} className={`${theme.palette.mode === 'dark' ? 'dashboard-dark-mode' : 'dashboard-light-mode'} ${animationLoaded ? 'dashboard-loaded' : 'dashboard-loading'}`}>
      <Container maxWidth="xl" className="dashboard-container-max-width">
        {isTemporaryAccess && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You are viewing this dashboard with temporary admin access.
          </Alert>
        )}

        {dataLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading your data...
            </Typography>
          </Box>
        ) : !uploadedData?.hasData ? (
          <Card sx={{ p: 6, textAlign: 'center', borderRadius: '30px' }}>
            <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Data Available
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {uploadedData?.message || 'Your data will appear here once uploaded by an administrator.'}
            </Typography>
          </Card>
        ) : (
          <>
            {/* Analytics Dashboard - 14 Boxes Layout (8+6) */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, position: 'relative', pt: 2 }} className="dashboard-intro-animation">
                <Typography variant="h5" fontWeight="bold" className={`dashboard-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'} dashboard-intro-animation`} sx={{ mt: 1 }}>
                  Viewer Dashboard
                </Typography>
                
                {/* Country Filter - Isolated */}
                <FormControl 
                variant="outlined" 
                size="small" 
                sx={{ 
                  minWidth: 200,
                    '& .MuiInputLabel-root': {
                      color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                      top: '2px',
                      transform: 'translate(14px, 12px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: theme.palette.mode === 'dark' ? '#191919' : 'inherit',
                        padding: '0 4px',
                        color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                      }
                    },
                    '& .MuiOutlinedInput-root': {
                      color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                      },
                      '& .MuiSelect-icon': {
                        color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                      }
                    }
                  }}
                >
                  <InputLabel>Filter by Country</InputLabel>
                  <Select
                    key={`country-select-${filterKey}`}
                    value={selectedCountryFilter}
                    onChange={(e) => setSelectedCountryFilter(e.target.value)}
                    label="Filter by Country"
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'right',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                      },
                      PaperProps: {
                        sx: {
                          backgroundColor: theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff',
                          border: theme.palette.mode === 'dark' ? '1px solid #ffffff' : '1px solid rgba(0, 0, 0, 0.23)',
                          position: 'fixed',
                          maxWidth: 'none',
                          minWidth: '200px',
                          transform: 'none',
                          boxShadow: theme.palette.mode === 'dark' ? '0px 4px 6px rgba(0, 0, 0, 0.3)' : '0px 4px 6px rgba(0, 0, 0, 0.1)',
                          '& .MuiMenuItem-root': {
                            color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                            }
                          }
                        }
                      }
                    }}
                  >
                    {availableCountries.map((country, index) => (
                      <MenuItem key={`${country}-${index}`} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                  </FormControl>
              </Box>
              
              {/* Filter Status Indicator */}
              {selectedCountryFilter !== 'All Countries' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Showing analytics for <strong>{selectedCountryFilter}</strong> - {formatNumber(filteredAnalyticsData.length)} leads
                </Alert>
              )}
              
              {/* First Row - 4 Key Metrics */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Box 1: Total Leads */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card className={`dashboard-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} ${theme.palette.mode === 'dark' ? 'dashboard-card dark-mode' : 'dashboard-card-light light-mode'} dashboard-card-animation dashboard-card-animation`}>
                    <CardContent className={`dashboard-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon - Absolute positioned top right */}
                      <Box className={`dashboard-box-iconbox-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-box-iconbox-bg-1-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomContactsIcon size={24} />
                      </Box>

                      {/* Title Section - Top Left */}
                      <Typography className={`dashboard-box-title-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Total Leads
                      </Typography>
                      
                      {/* Dynamic Number Section - Left aligned */}
                      <Typography className={`dashboard-box-number-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <AnimatedCounter value={insuranceAnalytics.totalLeads} formatValue={formatNumber} />
                      </Typography>
                      
                      {/* Descriptive Text Section - Bottom */}
                      <Typography className={`dashboard-box-description-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Total contacts
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Box 2: Interested Leads */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card className={`dashboard-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} ${theme.palette.mode === 'dark' ? 'dashboard-card dark-mode' : 'dashboard-card-light light-mode'} dashboard-card-animation dashboard-card-animation`}>
                    <CardContent className={`dashboard-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon - Absolute positioned top right */}
                      <Box className={`dashboard-box-iconbox-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-box-iconbox-bg-2-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomFavoriteIcon size={24} />
                      </Box>

                      {/* Title Section - Top Left */}
                      <Typography className={`dashboard-box-title-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Interested Leads
                      </Typography>
                      
                      {/* Dynamic Number Section - Left aligned */}
                      <Typography className={`dashboard-box-number-typography-alt-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <AnimatedCounter value={insuranceAnalytics.totalInterestedLeads} formatValue={formatNumber} />
                      </Typography>
                      
                      {/* Descriptive Text Section - Bottom */}
                      <Typography className={`dashboard-box-description-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Engaged leads
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>


                {/* Box 3: Brand Engagement */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card className={`dashboard-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} ${theme.palette.mode === 'dark' ? 'dashboard-card dark-mode' : 'dashboard-card-light light-mode'} dashboard-card-animation`}>
                    <CardContent className={`dashboard-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon - Absolute positioned top right */}
                      <Box className={`dashboard-box-iconbox-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-box-iconbox-bg-3-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomAnalyticsIcon size={24} />
                      </Box>

                      {/* Title Section - Top Left */}
                      <Typography className={`dashboard-box-title-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Brand Engagement
                      </Typography>
                      
                      {/* Dynamic Number Section - Left aligned */}
                      <Typography className={`dashboard-box-number-typography-alt-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <AnimatedCounter value={insuranceAnalytics.timesClientBrandSearched} />
                      </Typography>
                      
                      {/* Descriptive Text Section - Bottom */}
                      <Typography className={`dashboard-box-description-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Brand interaction count
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Box 4: Competitor Brand Engagement */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card className={`dashboard-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} ${theme.palette.mode === 'dark' ? 'dashboard-card dark-mode' : 'dashboard-card-light light-mode'} dashboard-card-animation`}>
                    <CardContent className={`dashboard-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon - Absolute positioned top right */}
                      <Box className={`dashboard-box-iconbox-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-box-iconbox-bg-4-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomCompetitionIcon size={24} />
                      </Box>

                      {/* Title Section - Top Left */}
                      <Typography className={`dashboard-box-title-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Competitor Brand<br />Engagement
                      </Typography>
                      
                      {/* Dynamic Number Section - Left aligned */}
                      <Typography className={`dashboard-box-number-typography-alt-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <AnimatedCounter value={insuranceAnalytics.competitorBrands} />
                      </Typography>
                      
                      {/* Descriptive Text Section - Bottom */}
                      <Typography className={`dashboard-box-description-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Competitor analytics
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Box 5: Website Visitors */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card className={`dashboard-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} ${theme.palette.mode === 'dark' ? 'dashboard-card dark-mode' : 'dashboard-card-light light-mode'} dashboard-card-animation`}>
                    <CardContent className={`dashboard-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon - Absolute positioned top right */}
                      <Box className={`dashboard-box-iconbox-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-box-iconbox-bg-5-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomWebIcon size={24} />
                      </Box>

                      {/* Title Section - Top Left */}
                      <Typography className={`dashboard-box-title-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Website Visitors
                      </Typography>
                      
                      {/* Dynamic Number Section - Left aligned */}
                      <Typography className={`dashboard-box-number-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <AnimatedCounter value={insuranceAnalytics.websiteVisitors} formatValue={formatNumber} />
                      </Typography>
                      
                      {/* Descriptive Text Section - Bottom */}
                      <Typography className={`dashboard-box-description-typography-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        Companies with websites
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

              </Grid>

              {/* Second Row - 5 Insights */}
              <Grid container spacing={3}>

                {/* Box 1: Top Industries */}
                <Grid item xs={12} sm={4} md={2.4}>
                  <Card className={`second-row-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-card-animation`}>
                    <CardContent className={`second-row-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon Left, Title Right Row */}
                      <Box className={`second-row-box-icon-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomIndustryIcon />
                        <Box className={`second-row-box-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          <Typography className={`second-row-box-title-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          Top Industries
                        </Typography>
                        </Box>
                      </Box>
                      {/* Data Text - Centered */}
                      <Tooltip title={insuranceAnalytics.topIndustries.slice(0, 3).join(', ') || 'N/A'} arrow>
                        <Typography className={`second-row-box-data-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          {insuranceAnalytics.topIndustries.slice(0, 3).join(', ') || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Box 2: Top Job Titles */}
                <Grid item xs={12} sm={4} md={2.4}>
                  <Card className={`second-row-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-card-animation`}>
                    <CardContent className={`second-row-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon Left, Title Right Row */}
                      <Box className={`second-row-box-icon-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomBadgeIcon />
                        <Box className={`second-row-box-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          <Typography className={`second-row-box-title-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          Top Job Titles
                        </Typography>
                        </Box>
                      </Box>
                      {/* Data Text - Centered */}
                      <Tooltip title={insuranceAnalytics.topJobPositions.slice(0, 3).join(', ') || 'No Data'} arrow>
                        <Typography className={`second-row-box-data-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          {insuranceAnalytics.topJobPositions.slice(0, 3).join(', ') || 'No Data'}
                        </Typography>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Box 3: Top Intent Keyword (7 Days) */}
                <Grid item xs={12} sm={4} md={2.4}>
                  <Card className={`second-row-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-card-animation`}>
                    <CardContent className={`second-row-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon Left, Title with Time Period Row */}
                      <Box className={`second-row-box-icon-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomTrendingUpIcon />
                        <Box className={`second-row-box-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          <Typography className={`second-row-box-title-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                            Top Intent Keyword
                          </Typography>
                          <Typography className={`second-row-box-subtitle-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                            7 Days
                          </Typography>
                        </Box>
                      </Box>
                      {/* Data Text - Centered */}
                      <Tooltip title={insuranceAnalytics.topIntent7Days.replace('Most Searched (7d): ', '')} arrow>
                        <Typography className={`second-row-box-data-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          {insuranceAnalytics.topIntent7Days.replace('Most Searched (7d): ', '')}
                        </Typography>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Box 4: Top Intent Keyword (30 Days) */}
                <Grid item xs={12} sm={4} md={2.4}>
                  <Card className={`second-row-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-card-animation`}>
                    <CardContent className={`second-row-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon Left, Title with Time Period Row */}
                      <Box className={`second-row-box-icon-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomTrophyIcon />
                        <Box className={`second-row-box-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          <Typography className={`second-row-box-title-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                            Top Intent Keyword
                          </Typography>
                          <Typography className={`second-row-box-subtitle-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                            30 Days
                          </Typography>
                        </Box>
                      </Box>
                      {/* Data Text - Centered */}
                      <Tooltip title={insuranceAnalytics.topIntent30Days.replace('Most Searched (30d): ', '')} arrow>
                        <Typography className={`second-row-box-data-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          {insuranceAnalytics.topIntent30Days.replace('Most Searched (30d): ', '')}
                        </Typography>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Box 5: Top Intent Keyword (90 Days) */}
                <Grid item xs={12} sm={4} md={2.4}>
                  <Card className={`second-row-box-card-${theme.palette.mode === 'dark' ? 'dark' : 'light'} dashboard-card-animation`}>
                    <CardContent className={`second-row-box-cardcontent-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                      {/* Icon Left, Title with Time Period Row */}
                      <Box className={`second-row-box-icon-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                        <CustomStarIcon />
                        <Box className={`second-row-box-title-container-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          <Typography className={`second-row-box-title-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                            Top Intent Keyword
                          </Typography>
                          <Typography className={`second-row-box-subtitle-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                            90 Days
                          </Typography>
                        </Box>
                      </Box>
                      {/* Data Text - Centered */}
                      <Tooltip title={insuranceAnalytics.topIntent90Days.replace('Most Searched (90d): ', '')} arrow>
                        <Typography className={`second-row-box-data-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                          {insuranceAnalytics.topIntent90Days.replace('Most Searched (90d): ', '')}
                        </Typography>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Top Keywords Section - Only show when "All Countries" is selected */}
              {selectedCountryFilter === 'All Countries' && insuranceAnalytics.primaryInterests && insuranceAnalytics.primaryInterests.length > 0 && 
               insuranceAnalytics.primaryInterests[0] !== 'No Data' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" className={`top-keywords-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold' }} gutterBottom>
                    Top Keywords (All Countries)
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': {
                      height: 6,
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      borderRadius: 3,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0,0,0,0.3)',
                      borderRadius: 3,
                    },
                  }}>
                    {insuranceAnalytics.primaryInterests.slice(0, 12).map((keyword, index) => (
                      <Chip
                        key={index}
                        label={keyword}
                        variant="outlined"
                        sx={{
                          fontWeight: 'bold',
                          borderWidth: 2,
                          flexShrink: 0,
                          '&:hover': {
                            transform: 'scale(1.05)',
                            transition: 'transform 0.2s'
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

            </Box>

            {/* Data Viewer with Toggle */}
            {allData.length > 0 && (
              <Box>
                {/* View Toggle */}
                <Box 
                  className={`data-viewer-header ${theme.palette.mode === 'dark' ? 'dark-mode' : ''} data-viewer-intro`}
                  sx={{ 
                    mb: 2
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" className={`data-viewer-title-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1
                    }}>
                      Data Viewer ({formatNumber(filteredAnalyticsData.length)} leads{selectedCountryFilter !== 'All Countries' && ` - ${selectedCountryFilter}`})
                    </Typography>
                    
                    {/* Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* Country Sort Buttons */}
                      {viewMode === 'cards' && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {/* Alphabetical Sort Button (A-Z) */}
                          <Tooltip title="Sort countries alphabetically (A-Z)">
                            <Button
                              className={`country-sort-button ${theme.palette.mode === 'dark' ? 'dark-mode' : ''} ${theme.palette.mode === 'light' && countrySortType === 'alphabetical' ? 'dashboard-toggle-button-light-active' : ''}`}
                              variant={countrySortType === 'alphabetical' ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => setCountrySortType('alphabetical')}
                              sx={{
                                minWidth: 50,
                                width: 50,
                                height: 50,
                                padding: 0,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                border: '2px solid',
                                borderColor: countrySortType === 'alphabetical' ? 'primary.main' : 'divider',
                                '&:hover': {
                                  transform: 'none',
                                  opacity: 0.9
                                }
                              }}
                            >
                              A-Z
                            </Button>
                          </Tooltip>
                          
                          {/* Numerical Sort Button - Always clickable */}
                          <Tooltip title={`Sort by company count (${numericalSortOrder === 'desc' ? 'most first' : 'least first'})`}>
                            <Button
                              className={`country-sort-button ${theme.palette.mode === 'dark' ? 'dark-mode' : ''} ${theme.palette.mode === 'light' && countrySortType === 'numerical' ? 'dashboard-toggle-button-light-active' : ''}`}
                              variant={countrySortType === 'numerical' ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => {
                                if (countrySortType === 'numerical') {
                                  // If already numerical, toggle the order
                                  toggleNumericalOrder();
                                } else {
                                  // If switching from alphabetical, set to numerical
                                  setCountrySortType('numerical');
                                }
                              }}
                              sx={{
                                minWidth: 50,
                                width: 50,
                                height: 50,
                                padding: 0,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                border: '2px solid',
                                borderColor: countrySortType === 'numerical' ? 'primary.main' : 'divider',
                                opacity: countrySortType === 'alphabetical' ? 0.7 : 1,
                                '&:hover': {
                                  transform: 'none',
                                  opacity: 1
                                }
                              }}
                            >
                              {getNumericalSortIcon()}
                            </Button>
                          </Tooltip>
                          
                          {/* Website Engagement Filter Button */}
                          <Tooltip title={showOnlyWebsiteEngagement ? "Showing companies with website visitors only" : "Show only companies with website visitors"}>
                            <Button
                              className={`country-sort-button ${theme.palette.mode === 'dark' ? 'dark-mode' : ''} ${theme.palette.mode === 'light' && showOnlyWebsiteEngagement ? 'dashboard-toggle-button-light-active' : ''}`}
                              variant={showOnlyWebsiteEngagement ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => setShowOnlyWebsiteEngagement(!showOnlyWebsiteEngagement)}
                              sx={{
                                minWidth: 50,
                                width: 50,
                                height: 50,
                                padding: 0,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                border: '2px solid',
                                borderColor: showOnlyWebsiteEngagement ? 'primary.main' : 'divider',
                                '&:hover': {
                                  transform: 'none',
                                  opacity: 0.9
                                }
                              }}
                            >
                              WWW
                            </Button>
                          </Tooltip>
                          
                          {/* Latest Updated Sort Button */}
                          <Tooltip title={countrySortType === 'latestUpdated' ? "Sorting by latest updated" : "Sort companies by latest lead upload"}>
                            <Button
                              className={`country-sort-button ${theme.palette.mode === 'dark' ? 'dark-mode' : ''} ${theme.palette.mode === 'light' && countrySortType === 'latestUpdated' ? 'dashboard-toggle-button-light-active' : ''}`}
                              variant={countrySortType === 'latestUpdated' ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => {
                                if (countrySortType === 'latestUpdated') {
                                  setCountrySortType('numerical');
                                } else {
                                  setCountrySortType('latestUpdated');
                                }
                              }}
                              sx={{
                                minWidth: 50,
                                width: 50,
                                height: 50,
                                padding: 0,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                border: '2px solid',
                                ...(theme.palette.mode === 'dark' && {
                                  color: countrySortType === 'latestUpdated' ? '#000000' : '#ffffff',
                                  borderColor: '#ffffff',
                                  backgroundColor: countrySortType === 'latestUpdated' ? '#ffffff' : 'transparent',
                                }),
                                ...(theme.palette.mode === 'light' && {
                                  borderColor: countrySortType === 'latestUpdated' ? 'primary.main' : 'divider',
                                }),
                                '&:hover': {
                                  transform: 'none',
                                  opacity: 0.9
                                }
                              }}
                            >
                              📈
                            </Button>
                          </Tooltip>
                        </Box>
                      )}
                      
                      {/* View Mode Toggle */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          className={`dashboard-toggle-button ${theme.palette.mode === 'dark' ? 'dark-mode' : ''} ${theme.palette.mode === 'light' && viewMode === 'cards' ? 'dashboard-toggle-button-light-active' : ''}`}
                          variant={viewMode === 'cards' ? 'contained' : 'outlined'}
                          size="small" 
                          onClick={() => setViewMode('cards')}
                          startIcon={<CardViewIcon />}
                        >
                          Cards
                        </Button>
                        <Button
                          className={`dashboard-toggle-button ${theme.palette.mode === 'dark' ? 'dark-mode' : ''} ${theme.palette.mode === 'light' && viewMode === 'table' ? 'dashboard-toggle-button-light-active' : ''}`}
                          variant={viewMode === 'table' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setViewMode('table')}
                          startIcon={<TableViewIcon />}
                          sx={{
                            ...(theme.palette.mode === 'dark' && {
                              color: viewMode === 'table' ? '#000000' : '#ffffff',
                              borderColor: '#ffffff',
                              backgroundColor: viewMode === 'table' ? '#ffffff' : 'transparent',
                              '& .MuiSvgIcon-root': {
                                color: viewMode === 'table' ? '#000000' : '#ffffff'
                              }
                            })
                          }}
                        >
                          Table
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                

                {/* Card View */}
                {viewMode === 'cards' && (
                  <Box 
                    className={`data-viewer-cards ${theme.palette.mode === 'dark' ? 'dark-mode' : ''}`}>
                    {(() => {
                      // Group data by country, then by company
                      // Use centralized getFieldValue function

                      // Get upload date from sheet data for tracking latest updates
                      const sheet1 = uploadedData?.sheets?.[0];
                      const uploadDate = sheet1?.uploadDate ? new Date(sheet1.uploadDate) : new Date();
                      
                      // Group data by country first, then by company
                      const countriesMap = new Map();
                      allData.forEach((row, index) => {
                        // Extract country - use only Company Country to avoid person-level Country noise
                        const country = getFieldValue(row, [
                          'Company Country'
                        ]) || 'Unknown Country';
                        const companyName = getFieldValue(row, ['Company Name', 'Company', 'Organization', 'Employer']) || 'Unknown Company';
                        
                        if (!countriesMap.has(country)) {
                          countriesMap.set(country, new Map());
                        }
                        
                        const companiesInCountry = countriesMap.get(country);
                        if (!companiesInCountry.has(companyName)) {
                          companiesInCountry.set(companyName, {
                            name: companyName,
                            country: country,
                            allKeywords: new Set<string>(),
                            keywords: new Set<string>(),
                            industries: new Set<string>(),
                            totalLeads: 0,
                            allData: [],
                            latestUploadDate: uploadDate
                          } as CompanyData);
                        }
                        
                        const company = companiesInCountry.get(companyName);
                        company.allData.push(row);
                        company.totalLeads += 1;
                        // Update latest upload date if this upload is newer
                        if (uploadDate && (!company.latestUploadDate || uploadDate > company.latestUploadDate)) {
                          company.latestUploadDate = uploadDate;
                        }
                        
                        
                        // SMART: Extract keywords with priority to avoid duplicates - prioritize Last 7 days
                        const allColumns = Object.keys(row);
                        
                        // Define buying intent columns in priority order (Last 7 days first)
                        const prioritizedBuyingIntentColumns = [
                          'Last 7 Days', // Priority 1: Last 7 days (most recent)
                          'Last 30 Days', // Priority 2: Last 30 days
                          'Last 90 Days', // Priority 3: Last 90 days
                          'Buying Intent' // Priority 5: Legacy standalone column
                        ];
                        
                        // Also check for any other buying intent columns that might exist
                        const otherBuyingIntentColumns = allColumns.filter(col => {
                          const lowerCol = col.toLowerCase();
                          return (
                            (lowerCol.includes('buying') && lowerCol.includes('intent') && 
                             (lowerCol.includes('7') || lowerCol.includes('30') || lowerCol.includes('90'))) ||
                            (lowerCol.includes('last') && (lowerCol.includes('7') || lowerCol.includes('30') || lowerCol.includes('90')) &&
                             lowerCol.includes('day'))
                          ) && !prioritizedBuyingIntentColumns.includes(col);
                        });
                        
                        // Combine prioritized columns with other columns
                        const allBuyingIntentColumns = [...prioritizedBuyingIntentColumns, ...otherBuyingIntentColumns];
                        
                        // Process buying intent columns in priority order, stopping at first match to prevent duplicates
                        let hasProcessedBuyingIntent = false;
                        for (const column of allBuyingIntentColumns) {
                          if (hasProcessedBuyingIntent) break; // Stop after first successful processing
                          
                          const value = row[column];
                          if (value && value !== 'N/A' && value !== '') {
                            hasProcessedBuyingIntent = true; // Mark as processed to prevent duplicates
                            
                            // STEP 1: Clean the entire value - remove ALL numbers in parentheses FIRST
                            let cleanedValue = String(value).trim();
                            
                            // Remove ALL occurrences of numbers in parentheses: (5), (133), (1234), etc.
                            cleanedValue = cleanedValue.replace(/\s*\(\d+\)/g, '');
                            
                            // Remove any standalone parentheses that might be left
                            cleanedValue = cleanedValue.replace(/\(\s*\)/g, '');
                            
                            // STEP 2: Split by delimiters
                            let keywords: string[] = [];
                            
                            if (cleanedValue.includes(',')) {
                              keywords = cleanedValue.split(',');
                            } else if (cleanedValue.includes(';')) {
                              keywords = cleanedValue.split(';');
                            } else if (cleanedValue.includes('|')) {
                              keywords = cleanedValue.split('|');
                            } else {
                              // Single value
                              keywords = [cleanedValue];
                            }
                            
                            // STEP 3: Final cleaning and add to set
                            keywords.forEach(keyword => {
                              let cleaned = keyword.trim();
                              
                              // Remove all trailing punctuation (., !, ?, ;, :, etc.)
                              cleaned = cleaned.replace(/[.,!?;:]+$/g, '');
                              
                              // Remove leading punctuation
                              cleaned = cleaned.replace(/^[.,!?;:]+/g, '');
                              
                              // Trim again after punctuation removal
                              cleaned = cleaned.trim();
                              
                              const cleanedLower = cleaned.toLowerCase();
                              
                              // Filter out invalid keywords
                              const isInvalid = (
                                !cleaned || // Empty
                                cleaned.length === 0 || // Zero length
                                cleanedLower === 'n/a' || // N/A variations
                                cleanedLower === 'na' || // NA
                                cleanedLower === 'n\\a' || // N\A
                                cleanedLower === 'null' || // null
                                cleanedLower === 'undefined' || // undefined
                                cleanedLower === 'none' || // none
                                cleaned === '-' || // dash
                                cleaned === '_' || // underscore
                                cleanedLower === companyName.toLowerCase() // Company's own name
                              );
                              
                              if (!isInvalid) {
                                company.allKeywords.add(cleaned);
                                company.keywords.add(cleaned);
                              }
                            });
                          }
                        }

                        // Extract industry keywords from Company Industry column
                        const industryColumns = [
                          'Company Industry', 'Industry', 'Business Industry', 
                          'Company Sector', 'Sector', 'Business Sector',
                          'Industry Type', 'Business Type', 'Company Type'
                        ];
                        
                        industryColumns.forEach(column => {
                          const value = row[column];
                          if (value && value !== 'N/A' && value !== '') {
                            // DON'T split industries - keep them as complete phrases
                            const cleanedIndustry = String(value)
                              .replace(/[{}[\]()<>"'`~!@#$%^&*+=|\\:;?/]/g, '') // Remove unwanted chars but NOT commas
                              .replace(/\s+/g, ' ')
                              .trim();
                            
                            if (cleanedIndustry && cleanedIndustry.length > 2) {
                              company.industries.add(cleanedIndustry);
                            }
                          }
                        });

                        // STEP 2: Intersect with Sheet 2 Generic Keywords (if available)
                        // Only keep company keywords that also appear in Sheet 2 Generic across any upload
                        if (buyingIntentData?.hasData) {
                          const sheet2GenericNormalized = new Set<string>(
                            buyingIntentData.buyingIntents
                              .filter((intent: any) => String(intent.category).trim() === 'Generic')
                              .map((intent: any) => normalizeKeywordForMatch(String(intent.keyword || '')))
                              .filter((k: string) => k.length > 0)
                          );
                          if (sheet2GenericNormalized.size > 0) {
                            const intersected = new Set<string>();
                            (Array.from(company.keywords) as string[]).forEach((existingKeyword: string) => {
                              const normalizedExisting = normalizeKeywordForMatch(existingKeyword);
                              if (normalizedExisting && sheet2GenericNormalized.has(normalizedExisting)) {
                                intersected.add(existingKeyword);
                              }
                            });
                            company.keywords = intersected;
                          }
                        }
                      });

                      // Convert to array and sort countries alphabetically
                      // Sort countries based on current sort order
                      const countries = Array.from(countriesMap.entries())
                        .filter(([country, companiesInCountry]) => companiesInCountry.size > 0)
                        .sort(([countryA, companiesA], [countryB, companiesB]) => {
                          if (countrySortType === 'alphabetical') {
                            return countryA.localeCompare(countryB); // Alphabetical
                          } else {
                            // Numerical sorting by company count
                            return numericalSortOrder === 'desc' 
                              ? companiesB.size - companiesA.size // Most companies first
                              : companiesA.size - companiesB.size; // Least companies first
                          }
                        });

                      return countries.filter(([countryName]) => {
                          // If "All Countries" is selected, show all countries
                          if (selectedCountryFilter === 'All Countries') {
                            return true;
                          }
                          // If a specific country is selected, only show that country
                          return countryName === selectedCountryFilter;
                        })
                        .map(([countryName, companiesMap], countryIndex) => {
                        // Sort companies based on sort type
                        let allCompanies = Array.from(companiesMap.values()) as CompanyData[];
                        if (countrySortType === 'latestUpdated') {
                          // Sort by latest upload date (newest first)
                          allCompanies = allCompanies.sort((a, b) => {
                            const dateA = a.latestUploadDate ? new Date(a.latestUploadDate).getTime() : 0;
                            const dateB = b.latestUploadDate ? new Date(b.latestUploadDate).getTime() : 0;
                            return dateB - dateA; // Descending (newest first)
                          });
                        } else if (countrySortType === 'alphabetical') {
                          allCompanies = allCompanies.sort((a, b) => a.name.localeCompare(b.name));
                        } else {
                          // Default: numerical sort by total leads
                          allCompanies = allCompanies.sort((a, b) => {
                            const diff = b.totalLeads - a.totalLeads;
                            return numericalSortOrder === 'desc' ? diff : -diff;
                          });
                        }
                        let filteredCompanies = filterCompaniesForCountry(allCompanies, countryName);
                        
                        // Apply website engagement filter if active
                        if (showOnlyWebsiteEngagement) {
                          filteredCompanies = filteredCompanies.filter(company => {
                            // Check if this company has at least one lead with Website Engagement data
                            const hasEngagement = company.allData.some(row => {
                              const websiteEngagement = getFieldValue(row, COMMON_FIELD_COMBINATIONS.WEBSITE_ENGAGEMENT as unknown as string[]);
                              return websiteEngagement && websiteEngagement !== 'N/A' && websiteEngagement !== '' && websiteEngagement.trim().length > 0;
                            });
                            return hasEngagement;
                          });
                        }
                        const currentFilters = countryFilters[countryName] || { searchQuery: '', selectedBuyingIntents: [], selectedIndustries: [], selectedBranding: [], timeRange: 'last7days' };
                        const availableBuyingIntents = getBuyingIntentsForCountry(allCompanies);
                        const availableIndustries = getIndustriesForCountry(allCompanies);
                        const availableBranding = getBrandingForCountry(filteredCompanies);
                        
                        // Get country-specific data for charts
                        const countrySpecificData = allData.filter(row => {
                          // Use centralized getFieldValue function
                          const rowCountry = getFieldValue(row, ['Company Country']) || 'Unknown Country';
                          return rowCountry === countryName;
                        });
                        
                        return (
                          <Box key={countryName} sx={{ mb: 6 }}>
                            {/* Country Header */}
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                borderBottom: `3px solid ${theme.palette.primary.main}`,
                                pb: 2,
                                mb: 2
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography 
                                    variant="h4" 
                                    sx={{ 
                                      fontWeight: 'bold',
                                      color: theme.palette.primary.main,
                                    }}
                                  >
                                    {countryName}
                                  </Typography>
                                  <Chip 
                                    className="company-count-chip"
                                    label={`${filteredCompanies.length} / ${allCompanies.length} companies`} 
                                    color="primary" 
                                    variant="outlined"
                                    size="small"
                                  />
                                </Box>
                                
                                {/* Clear Filters Button */}
                                {(currentFilters.searchQuery || currentFilters.selectedBuyingIntents.length > 0 || currentFilters.selectedIndustries.length > 0 || currentFilters.selectedBranding.length > 0) && (
                                  <Button
                                    className={`admin-filter-button ${theme.palette.mode === 'dark' ? 'admin-reset-filters-button-dark' : 'admin-reset-filters-button-light'}`}
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ClearIcon />}
                                    onClick={() => clearCountryFilters(countryName)}
                                    sx={{ ml: 2 }}
                                  >
                                    Clear Filters
                                  </Button>
                                )}
                              </Box>
                              
                              {/* Country-Specific Charts */}
                              <Box sx={{ mb: 3 }}>
                                {/* Time Range Selector */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                  <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel id={`time-range-label-${countryName}`}>Time Range</InputLabel>
                                    <Select
                                      labelId={`time-range-label-${countryName}`}
                                      value={currentFilters.timeRange}
                                      label="Time Range"
                                      onChange={(e) => updateCountryTimeRange(countryName, e.target.value as 'last7days' | 'last30days' | 'last90days')}
                                    >
                                      <MenuItem value="last7days">Last 7 Days</MenuItem>
                                      <MenuItem value="last30days">Last 30 Days</MenuItem>
                                      <MenuItem value="last90days">Last 90 Days</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>
                                
                                <div className="dashboard-dark-charts-wrapper">
                                  {/* Second Row - Two Columns */}
                                  <div className="dashboard-dark-second-row">
                                    {/* Left Column */}
                                    <div className="dashboard-dark-left-column">
                                      <div className="dashboard-dark-column-title">
                                        <h2>Generic Buying Intent - {countryName}</h2>
                                      </div>
                                      <div className="dashboard-dark-chart-with-keywords">
                                        <div className="dashboard-dark-donut-section">
                                          <BeautifulDonutChart 
                                            data={countrySpecificData} 
                                            groupBy="intent" 
                                            title=""
                                            timeRange={currentFilters.timeRange}
                                          />
                                        </div>
                                        <div className="dashboard-dark-keywords-data">
                                          {/* Intent Keywords Data - Placeholder for future abbreviated view */}
                                          <div className="dashboard-dark-keywords-content" style={{ display: 'none' }}>
                                            <Typography variant="h6" sx={{ 
                                              color: theme.palette.text.primary, 
                                              fontSize: '16px',
                                              fontWeight: 600,
                                              mb: 2
                                            }}>
                                              Intent Keywords
                                            </Typography>
                                            <div className="dashboard-dark-keywords-list">
                                              {/* This will show abbreviated keyword data */}
                                              <Typography variant="body2" sx={{ 
                                                color: theme.palette.text.secondary,
                                                fontSize: '14px'
                                              }}>
                                                Top keywords will be displayed here
                                              </Typography>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Right Column */}
                                    <div className="dashboard-dark-right-column">
                                      <div className="dashboard-dark-right-header">
                                        <h2 className={`intent-keywords-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>Intent Keywords - {countryName}</h2>
                                      </div>
                                      <div className={`dashboard-dark-keywords-container-${theme.palette.mode}`}>
                                        <RankedListChart 
                                          data={countrySpecificData} 
                                          groupBy="intent" 
                                          title=""
                                          timeRange={currentFilters.timeRange}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Box>
                              
                              {/* Search and Filter Controls */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Search Field */}
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <TextField
                                    size="small"
                                    placeholder={`Search in ${getCountryDisplayName(countryName)}`}
                                    value={currentFilters.searchQuery}
                                    onChange={(e) => updateCountrySearch(countryName, e.target.value)}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <SearchIcon sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{ 
                                      minWidth: 300,
                                      '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'background.paper',
                                        '&:hover': {
                                          backgroundColor: 'action.hover',
                                        },
                                      }
                                    }}
                                  />
                                  
                                  {/* Filter Button */}
                                  <Button
                                    className="admin-filter-button"
                                    variant={currentFilters.selectedBuyingIntents.length > 0 || currentFilters.selectedIndustries.length > 0 || currentFilters.selectedBranding.length > 0 ? 'contained' : 'outlined'}
                                    size="small"
                                    startIcon={<FilterIcon />}
                                    onClick={() => setExpandedFilters(prev => ({ ...prev, [countryName]: !prev[countryName] }))}
                                    endIcon={expandedFilters[countryName] ? <ExpandLess /> : <ExpandMore />}
                                    sx={{
                                      px: 2,
                                      py: 1,
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontWeight: 500
                                    }}
                                  >
                                    Filter
                                    {(currentFilters.selectedBuyingIntents.length + currentFilters.selectedIndustries.length + currentFilters.selectedBranding.length > 0) && 
                                      ` (${currentFilters.selectedBuyingIntents.length + currentFilters.selectedIndustries.length + currentFilters.selectedBranding.length})`
                                    }
                                  </Button>
                                </Box>
                                
                              </Box>
                              
                              {/* Expandable Filter Options */}
                              <Collapse in={expandedFilters[countryName]} timeout="auto" unmountOnExit>
                                <Box sx={{ 
                                  mt: 2, 
                                  p: 3, 
                                  bgcolor: 'background.paper', 
                                  border: 1, 
                                  borderColor: 'divider', 
                                  borderRadius: 3,
                                  boxShadow: theme.shadows[2]
                                }}>
                                  {/* Filter Selection Interface */}
                                  <Grid container spacing={4} sx={{ mb: 3 }}>
                                    {/* Left Column - Buying Intent Selection */}
                                    <Grid item xs={12} md={6}>
                                      <Typography 
                                        variant="subtitle1" 
                                        sx={{ 
                                          mb: 2, 
                                          fontWeight: 'bold', 
                                          fontSize: '15px',
                                          fontFamily: 'Nunito Sans, sans-serif',
                                          color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1
                                        }}>
                                        Buying Intent Filters
                                      </Typography>
                                      <Box sx={{ 
                                        maxHeight: 250, 
                                        overflowY: 'auto', 
                                        backgroundColor: 'background.paper',
                                        border: 1, 
                                        borderColor: 'divider', 
                                        borderRadius: 2, 
                                        p: 1 
                                      }}>
                                        {availableBuyingIntents.length === 0 ? (
                                          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', fontSize: '1rem', fontWeight: 600 }}>
                                            No Filter
                                          </Typography>
                                        ) : (
                                          availableBuyingIntents.map((intent) => (
                                            <FormControlLabel
                                              key={intent}
                                              control={
                                                <Checkbox
                                                  checked={currentFilters.selectedBuyingIntents.includes(intent)}
                                                  onChange={(e) => updateCountryFilter(countryName, 'buyingIntents', intent, e.target.checked)}
                                                  size="small"
                                                  icon={<Box sx={{ 
                                                    width: 18, 
                                                    height: 18, 
                                                    borderRadius: '50%', 
                                                    border: '2px solid #1B4672',
                                                    backgroundColor: 'transparent'
                                                  }} />}
                                                  checkedIcon={<Box sx={{ 
                                                    width: 18, 
                                                    height: 18, 
                                                    borderRadius: '50%', 
                                                    backgroundColor: '#1B4672',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold'
                                                  }}>✓</Box>}
                                                  sx={{ 
                                                    color: '#1B4672',
                                                    '&.Mui-checked': { 
                                                      color: '#1B4672',
                                                    }
                                                  }}
                                                />
                                              }
                                              label={intent}
                                              sx={{ 
                                                display: 'block', 
                                                m: 0.5,
                                                p: 0.5,
                                                borderRadius: 1,
                                                '&:hover': {
                                                  backgroundColor: 'action.hover'
                                                },
                                                '& .MuiFormControlLabel-label': { 
                                                  fontSize: '0.875rem',
                                                  lineHeight: 1.2
                                                } 
                                              }}
                                            />
                                          ))
                                        )}
                                      </Box>
                                    </Grid>
                                    
                                    {/* Right Column - Branding Selection */}
                                    <Grid item xs={12} md={6}>
                                      <Typography 
                                        variant="subtitle1" 
                                        sx={{ 
                                          mb: 2, 
                                          fontWeight: 'bold', 
                                          fontSize: '15px',
                                          fontFamily: 'Nunito Sans, sans-serif',
                                          color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1
                                        }}>
                                        Branding Filters
                                      </Typography>
                                      <Box sx={{ 
                                        maxHeight: 250, 
                                        overflowY: 'auto', 
                                        backgroundColor: 'background.paper',
                                        border: 1, 
                                        borderColor: 'divider', 
                                        borderRadius: 2, 
                                        p: 1 
                                      }}>
                                        {availableBranding.length === 0 ? (
                                          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', fontSize: '1rem', fontWeight: 600 }}>
                                            No Filter
                                          </Typography>
                                        ) : (
                                          availableBranding.map((branding) => (
                                            <FormControlLabel
                                              key={branding}
                                              control={
                                                <Checkbox
                                                  checked={currentFilters.selectedBranding.includes(branding)}
                                                  onChange={(e) => updateCountryFilter(countryName, 'branding', branding, e.target.checked)}
                                                  size="small"
                                                  icon={<Box sx={{ 
                                                    width: 18, 
                                                    height: 18, 
                                                    borderRadius: '50%', 
                                                    border: '2px solid #1B4672',
                                                    backgroundColor: 'transparent'
                                                  }} />}
                                                  checkedIcon={<Box sx={{ 
                                                    width: 18, 
                                                    height: 18, 
                                                    borderRadius: '50%', 
                                                    backgroundColor: '#1B4672',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold'
                                                  }}>✓</Box>}
                                                  sx={{ 
                                                    color: '#1B4672',
                                                    '&.Mui-checked': { 
                                                      color: '#1B4672',
                                                    }
                                                  }}
                                                />
                                              }
                                              label={branding}
                                              sx={{ 
                                                display: 'block', 
                                                m: 0.5,
                                                p: 0.5,
                                                borderRadius: 1,
                                                '&:hover': {
                                                  backgroundColor: 'action.hover'
                                                },
                                                '& .MuiFormControlLabel-label': { 
                                                  fontSize: '0.875rem',
                                                  lineHeight: 1.2
                                                } 
                                              }}
                                            />
                                          ))
                                        )}
                                      </Box>
                                    </Grid>
                                  </Grid>

                                  {/* Two Column Layout with Selected Filter Pills */}
                                  <Box sx={{ display: 'flex', gap: 3 }}>
                                    {/* Left Column - Selected Buying Intent Filters */}
                                    <Box sx={{ flex: 1 }}>
                                      <Typography 
                                        variant="subtitle1" 
                                        sx={{ 
                                          mb: 2, 
                                          fontWeight: 'bold', 
                                          fontSize: '15px',
                                          fontFamily: 'Nunito Sans, sans-serif',
                                          color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1
                                        }}>
                                        Selected Buying Intent Filters
                                      </Typography>
                                      {currentFilters.selectedBuyingIntents.length === 0 ? (
                                        <Box 
                                          className="pill-container-4-per-row"
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: '110px',
                                            padding: '12px 16px 18px 8px',
                                            marginTop: '10px',
                                            background: 'linear-gradient(135deg, rgba(255, 109, 141, 0.2) 0%, rgba(96, 201, 140, 0.2) 10%, rgba(230, 199, 23, 0.2) 20%, rgba(148, 47, 237, 0.2) 30%, rgba(67, 187, 239, 0.2) 40%, rgba(230, 23, 23, 0.2) 50%, rgba(255, 196, 0, 0.2) 60%, rgba(255, 119, 0, 0.2) 70%, rgba(23, 96, 230, 0.2) 80%, rgba(255, 109, 141, 0.2) 100%)',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                          }}
                                        >
                                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '1rem', fontWeight: 600 }}>
                                            No Filter
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <div 
                                          className="pill-container-4-per-row pill-container-scrollbar"
                                          style={{
                                            paddingBottom: '18px',
                                            paddingRight: '12px'
                                          }}>
                                          {currentFilters.selectedBuyingIntents.map((intent, index) => {
                                            const pillStyle = getPillColor(index);
                                            return (
                                              <MarqueeChip
                                                key={`selected-intent-${index}`}
                                                label={String(intent)}
                                                size="small"
                                                maxWidth={90}
                                                textColor={pillStyle.color}
                                                sx={{ 
                                                  backgroundColor: pillStyle.backgroundColor,
                                                  color: pillStyle.color,
                                                  border: `2px solid ${pillStyle.color}`,
                                                  cursor: 'pointer',
                                                  '&:hover': {
                                                    backgroundColor: pillStyle.backgroundColor,
                                                    transform: 'scale(1.05)',
                                                  }
                                                }}
                                                onClick={() => updateCountryFilter(countryName, 'buyingIntents', intent, false)}
                                              />
                                            );
                                          })}
                                        </div>
                                      )}
                                    </Box>
                                    
                                    {/* Right Column - Selected Branding Filters */}
                                    <Box sx={{ flex: 1 }}>
                                      <Typography 
                                        variant="subtitle1" 
                                        sx={{ 
                                          mb: 2, 
                                          fontWeight: 'bold', 
                                          fontSize: '15px',
                                          fontFamily: 'Nunito Sans, sans-serif',
                                          color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1
                                        }}>
                                        Selected Branding Filters
                                      </Typography>
                                      {currentFilters.selectedBranding.length === 0 ? (
                                        <Box 
                                          className="pill-container-4-per-row"
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: '110px',
                                            padding: '12px 16px 18px 8px',
                                            marginTop: '10px',
                                            background: 'linear-gradient(135deg, rgba(255, 109, 141, 0.2) 0%, rgba(96, 201, 140, 0.2) 10%, rgba(230, 199, 23, 0.2) 20%, rgba(148, 47, 237, 0.2) 30%, rgba(67, 187, 239, 0.2) 40%, rgba(230, 23, 23, 0.2) 50%, rgba(255, 196, 0, 0.2) 60%, rgba(255, 119, 0, 0.2) 70%, rgba(23, 96, 230, 0.2) 80%, rgba(255, 109, 141, 0.2) 100%)',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                          }}
                                        >
                                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '1rem', fontWeight: 600 }}>
                                            No Filter
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <div 
                                          className="pill-container-4-per-row pill-container-scrollbar"
                                          style={{
                                            paddingBottom: '18px',
                                            paddingRight: '12px'
                                          }}>
                                          {currentFilters.selectedBranding.map((branding, index) => {
                                            const pillStyle = getPillColor(index);
                                            return (
                                              <MarqueeChip
                                                key={`selected-branding-${index}`}
                                                label={String(branding)}
                                                size="small"
                                                maxWidth={90}
                                                textColor={pillStyle.color}
                                                sx={{ 
                                                  backgroundColor: pillStyle.backgroundColor,
                                                  color: pillStyle.color,
                                                  border: `2px solid ${pillStyle.color}`,
                                                  cursor: 'pointer',
                                                  '&:hover': {
                                                    backgroundColor: pillStyle.backgroundColor,
                                                    transform: 'scale(1.05)',
                                                  }
                                                }}
                                                onClick={() => updateCountryFilter(countryName, 'branding', branding, false)}
                                              />
                                            );
                                          })}
                                        </div>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              </Collapse>
                            </Box>
                            
                            {/* Companies Grid */}
                            {filteredCompanies.length === 0 ? (
                              <Box sx={{ 
                                width: '100%', 
                                textAlign: 'center', 
                                py: 6,
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                                borderRadius: 2,
                                border: `1px dashed ${theme.palette.divider}`
                              }}>
                                <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                  No companies found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {(currentFilters.searchQuery && currentFilters.searchQuery.length >= 3) || 
                                   currentFilters.selectedBuyingIntents.length > 0 || 
                                   currentFilters.selectedIndustries.length > 0 ||
                                   currentFilters.selectedBranding.length > 0
                                    ? 'Try adjusting your search or filters to see more results'
                                    : `No companies available in ${countryName}`
                                  }
                                </Typography>
                                {((currentFilters.searchQuery && currentFilters.searchQuery.length >= 3) || 
                                  currentFilters.selectedBuyingIntents.length > 0 || 
                                  currentFilters.selectedIndustries.length > 0 ||
                                  currentFilters.selectedBranding.length > 0) && (
                                  <Button
                                    className={`admin-filter-button ${theme.palette.mode === 'dark' ? 'admin-reset-filters-button-dark' : 'admin-reset-filters-button-light'}`}
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ClearIcon />}
                                    onClick={() => clearCountryFilters(countryName)}
                                    sx={{ mt: 2 }}
                                  >
                                    Clear All Filters
                                  </Button>
                                )}
                              </Box>
                            ) : (
                              <Grid container spacing={4}>
                                {filteredCompanies.map((company: CompanyData, companyIndex: number) => (
                                <Grid item xs={12} sm={6} md={4} lg={4} key={companyIndex}>
                                  <Card 
                                    className={`${theme.palette.mode === 'dark' ? 'company-card-dark-mode' : 'company-card-light-mode'} company-card-base-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}
                                    sx={{ 
                                      height: '100%',
                                      borderRadius: 2,
                                      transition: 'all 0.3s ease',
                                      border: theme.palette.mode === 'dark' ? 'none' : `1px solid ${theme.palette.divider}`,
                                      cursor: 'pointer',
                                      backgroundColor: theme.palette.mode === 'light' ? 'background.paper' : undefined,
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: theme.shadows[8],
                                        borderColor: theme.palette.primary.main,
                                      }
                                    }}
                                    onClick={() => handleCompanyClick(company)}
                                  >
                                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                      {/* Header with Company */}
                                      <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                            <Typography 
                                              variant="h6" 
                                              sx={{ 
                                                fontFamily: '"Nunito Sans", sans-serif',
                                                fontWeight: '900',
                                                fontSize: '23px',
                                                color: theme.palette.primary.main,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                              }}
                                              title={cleanDisplayValue(company.name, 'Company Name') as string}
                                            >
                                              {cleanDisplayValue(company.name, 'Company Name')}
                                            </Typography>
                                          </Box>
                                          <Chip 
                                            className={`company-leads-chip-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}
                                            label={`+ ${company.totalLeads}`}
                                            size="small" 
                                            sx={{ ml: 1 }}
                                          />
                                        </Box>
                                        <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? '#C8C8C8' : 'inherit' }} />
                                      </Box>

                                      {/* Keywords Section */}
                                      <Box className={`pill-container-wrapper-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}>
                                        {/* Buying Intent Keywords Row - Always show (Sheet 1 ∩ Sheet 2 Generic) */}
                                        <Box className="pill-container-wrapper" sx={{ mb: 2 }}>
                                          <Typography 
                                            variant="caption" 
                                            className={`pill-container-title ${theme.palette.mode === 'dark' ? 'pill-container-title-dark' : 'pill-container-title-light'}`}
                                          >
                                            Generic Buying Intent
                                          </Typography>
                                          {(() => {
                                            const filteredKeywords = getFilteredKeywords(company, countryName);
                                            if (filteredKeywords.length > 0) {
                                              return (
                                              <div 
                                                className="pill-container-3-per-row company-card-pill-scrollbar"
                                                style={{
                                                  overflowY: filteredKeywords.length > 6 ? 'auto' : 'hidden',
                                                  gap: '8px !important',
                                                  padding: '6px !important',
                                                  minHeight: '70px !important',
                                                  maxHeight: '70px !important',
                                                  height: '70px !important'
                                                }}
                                              >
                                                {filteredKeywords.map((keyword, index) => {
                                                const pillStyle = getPillColor(index);
                                                return (
                                                  <MarqueeChip
                                                    key={`keyword-${index}`}
                                                    label={String(keyword)}
                                                    size="small"
                                                    maxWidth={120}
                                                    textColor={pillStyle.color}
                                                    sx={{
                                                      backgroundColor: pillStyle.backgroundColor,
                                                      color: pillStyle.color,
                                                      fontSize: '0.75rem',
                                                      height: '28px',
                                                      width: 'auto',
                                                      minWidth: '60px',
                                                      border: pillStyle.border,
                                                      '& .MuiChip-label': {
                                                        py: 0.3,
                                                        px: 1,
                                                        fontWeight: 500,
                                                        textAlign: 'center',
                                                        lineHeight: 1.3,
                                                        fontSize: '0.75rem'
                                                      }
                                                    }}
                                                  />
                                                );
                                              })}
                                              </div>
                                              );
                                            } else {
                                              return (
                                                <Box 
                                                className="pill-container-3-per-row no-data-container"
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  minHeight: '70px',
                                                  maxHeight: '70px',
                                                  height: '70px',
                                                  padding: '6px',
                                                  marginTop: '10px',
                                                  background: 'linear-gradient(135deg, rgba(255, 109, 141, 0.2) 0%, rgba(96, 201, 140, 0.2) 10%, rgba(230, 199, 23, 0.2) 20%, rgba(148, 47, 237, 0.2) 30%, rgba(67, 187, 239, 0.2) 40%, rgba(230, 23, 23, 0.2) 50%, rgba(255, 196, 0, 0.2) 60%, rgba(255, 119, 0, 0.2) 70%, rgba(23, 96, 230, 0.2) 80%, rgba(255, 109, 141, 0.2) 100%)',
                                                  borderRadius: '8px',
                                                  border: '1px solid rgba(255, 255, 255, 0.3)',
                                                  position: 'relative',
                                                  overflow: 'hidden',
                                                  width: '100% !important',
                                                  minWidth: '100% !important',
                                                  maxWidth: '100% !important',
                                                  '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: '-100%',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                                                    animation: 'shimmer 2s infinite'
                                                  },
                                                  '@keyframes shimmer': {
                                                    '0%': { left: '-100%' },
                                                    '100%': { left: '100%' }
                                                  }
                                                }}
                                              >
                                                <Typography 
                                                  variant="body2" 
                                                  sx={{ 
                                                    fontSize: '0.9rem',
                                                    fontWeight: 700,
                                                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                                                    textAlign: 'center',
                                                    textShadow: theme.palette.mode === 'dark' ? '0 1px 2px rgba(0, 0, 0, 0.3)' : '0 1px 2px rgba(255, 255, 255, 0.5)',
                                                    zIndex: 1,
                                                    position: 'relative',
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                  }}
                                                >
                                                    No Data
                                                  </Typography>
                                                </Box>
                                              );
                                            }
                                          })()}
                                        </Box>

                                        {/* Branded Buying Intent Section - New Section */}
                                        <Box className="pill-container-wrapper" sx={{ mb: 2, mt: 3 }}>
                                          <Typography 
                                            variant="caption" 
                                            className={`pill-container-title ${theme.palette.mode === 'dark' ? 'pill-container-title-dark' : 'pill-container-title-light'}`}
                                          >
                                            Branded Buying Intent
                                          </Typography>
                                          {(() => {
                                            // Get branded keywords from Sheet 2 that match Sheet 1 buying intent keywords
                                            const brandedKeywords: string[] = [];
                                            
                                            if (buyingIntentData?.hasData && company.keywords.size > 0) {
                                              const sheet1Keywords = Array.from(company.allKeywords);
                                              const normalizedSheet1Keywords = new Set(
                                                sheet1Keywords.map(k => normalizeKeywordForMatch(String(k)))
                                              );

                                              buyingIntentData.buyingIntents.forEach((intent: any) => {
                                                const keyword = (intent.keyword || '').trim();
                                                const category = intent.category || '';

                                                if (category === 'Branded' && keyword) {
                                                  const normalizedKeyword = normalizeKeywordForMatch(keyword);

                                                  if (normalizedKeyword.length === 0) return;

                                                  if (normalizedSheet1Keywords.has(normalizedKeyword)) {
                                                    const isDuplicate = brandedKeywords.some(
                                                      (k) => normalizeKeywordForMatch(k) === normalizedKeyword
                                                    );
                                                    if (!isDuplicate) {
                                                      brandedKeywords.push(keyword);
                                                    }
                                                  }
                                                }
                                              });
                                            }

                                            return brandedKeywords.length > 0 ? (
                                              <div 
                                                className="pill-container-3-per-row company-card-pill-scrollbar"
                                                style={{
                                                  overflowY: brandedKeywords.length > 6 ? 'auto' : 'hidden',
                                                  gap: '8px !important',
                                                  padding: '6px !important',
                                                  minHeight: '70px !important',
                                                  maxHeight: '70px !important',
                                                  height: '70px !important'
                                                }}
                                              >
                                                {brandedKeywords.map((keyword, index) => {
                                                  const pillStyle = getPillColor(index);
                                                  return (
                                                    <MarqueeChip
                                                      key={`branded-keyword-${index}`}
                                                      label={String(keyword)}
                                                      size="small"
                                                      maxWidth={120}
                                                      textColor={pillStyle.color}
                                                      sx={{
                                                        backgroundColor: pillStyle.backgroundColor,
                                                        color: pillStyle.color,
                                                        fontSize: '0.75rem',
                                                        height: '28px',
                                                        width: 'auto',
                                                        minWidth: '60px',
                                                        border: pillStyle.border,
                                                        '& .MuiChip-label': {
                                                          py: 0.3,
                                                          px: 1,
                                                          fontWeight: 500,
                                                          textAlign: 'center',
                                                          lineHeight: 1.3,
                                                          fontSize: '0.75rem'
                                                        }
                                                      }}
                                                    />
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              <Box 
                                                className="pill-container-3-per-row no-data-container"
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  minHeight: '70px',
                                                  maxHeight: '70px',
                                                  height: '70px',
                                                  padding: '6px',
                                                  marginTop: '10px',
                                                  background: 'linear-gradient(135deg, rgba(255, 109, 141, 0.2) 0%, rgba(96, 201, 140, 0.2) 10%, rgba(230, 199, 23, 0.2) 20%, rgba(148, 47, 237, 0.2) 30%, rgba(67, 187, 239, 0.2) 40%, rgba(230, 23, 23, 0.2) 50%, rgba(255, 196, 0, 0.2) 60%, rgba(255, 119, 0, 0.2) 70%, rgba(23, 96, 230, 0.2) 80%, rgba(255, 109, 141, 0.2) 100%)',
                                                  borderRadius: '8px',
                                                  border: '1px solid rgba(255, 255, 255, 0.3)',
                                                  position: 'relative',
                                                  overflow: 'hidden',
                                                  width: '100% !important',
                                                  minWidth: '100% !important',
                                                  maxWidth: '100% !important',
                                                  '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: '-100%',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                                                    animation: 'shimmer 2s infinite'
                                                  },
                                                  '@keyframes shimmer': {
                                                    '0%': { left: '-100%' },
                                                    '100%': { left: '100%' }
                                                  }
                                                }}
                                              >
                                                <Typography 
                                                  variant="body2" 
                                                  sx={{ 
                                                    fontSize: '0.9rem',
                                                    fontWeight: 700,
                                                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                                                    textAlign: 'center',
                                                    textShadow: theme.palette.mode === 'dark' ? '0 1px 2px rgba(0, 0, 0, 0.3)' : '0 1px 2px rgba(255, 255, 255, 0.5)',
                                                    zIndex: 1,
                                                    position: 'relative',
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                  }}
                                                >
                                                  No Data
                                                </Typography>
                                              </Box>
                                            );
                                          })()}
                                        </Box>

                                        {/* Separator Border between Buying Intent and Industry - Always show */}
                                        <Box 
                                          className="buying-intent-industry-separator"
                                          sx={{ 
                                            borderTop: `1px solid ${theme.palette.mode === 'dark' ? '#C8C8C8' : theme.palette.divider}`,
                                            mb: 3,
                                            mt: 2
                                          }} 
                                        />

                                        {/* Industry Keywords Row - Always show */}
                                        <Box className="pill-container-wrapper">
                                          <Typography 
                                            variant="caption" 
                                            className={`pill-container-title ${theme.palette.mode === 'dark' ? 'pill-container-title-dark' : 'pill-container-title-light'}`}
                                          >
                                            Industry
                                          </Typography>
                                          {company.industries.size > 0 ? (
                                            <div 
                                              className="pill-container-3-per-row company-card-pill-scrollbar"
                                              style={{
                                                overflowY: getFilteredIndustries(company, countryName).length > 6 ? 'auto' : 'hidden',
                                                gap: '8px !important',
                                                padding: '6px !important',
                                                minHeight: '70px !important',
                                                maxHeight: '70px !important',
                                                height: '70px !important'
                                              }}
                                            >
                                              {getFilteredIndustries(company, countryName).map((industry, index) => {
                                                const pillStyle = getPillColor(index);
                                                return (
                                                  <MarqueeChip
                                                    key={`industry-${index}`}
                                                    label={String(industry)}
                                                    size="small"
                                                    maxWidth={120}
                                                    textColor={pillStyle.color}
                                                    sx={{
                                                      backgroundColor: pillStyle.backgroundColor,
                                                      color: pillStyle.color,
                                                      fontSize: '0.75rem',
                                                      height: '28px',
                                                      width: 'auto',
                                                      minWidth: '60px',
                                                      border: pillStyle.border,
                                                      '& .MuiChip-label': {
                                                        py: 0.3,
                                                        px: 1,
                                                        fontWeight: 500,
                                                        textAlign: 'center',
                                                        lineHeight: 1.3,
                                                        fontSize: '0.75rem'
                                                      }
                                                    }}
                                                  />
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <Box 
                                              className="pill-container-3-per-row"
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minHeight: '70px',
                                                maxHeight: '70px',
                                                height: '70px',
                                                padding: '6px',
                                                marginTop: '10px',
                                                background: 'linear-gradient(135deg, rgba(255, 109, 141, 0.2) 0%, rgba(96, 201, 140, 0.2) 10%, rgba(230, 199, 23, 0.2) 20%, rgba(148, 47, 237, 0.2) 30%, rgba(67, 187, 239, 0.2) 40%, rgba(230, 23, 23, 0.2) 50%, rgba(255, 196, 0, 0.2) 60%, rgba(255, 119, 0, 0.2) 70%, rgba(23, 96, 230, 0.2) 80%, rgba(255, 109, 141, 0.2) 100%)',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&::before': {
                                                  content: '""',
                                                  position: 'absolute',
                                                  top: 0,
                                                  left: '-100%',
                                                  width: '100%',
                                                  height: '100%',
                                                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                                                  animation: 'shimmer 2s infinite'
                                                },
                                                '@keyframes shimmer': {
                                                  '0%': { left: '-100%' },
                                                  '100%': { left: '100%' }
                                                }
                                              }}
                                            >
                                              <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                  fontSize: '0.75rem',
                                                  fontWeight: 700,
                                                  color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                                                  textAlign: 'center',
                                                  textShadow: theme.palette.mode === 'dark' ? '0 1px 2px rgba(0, 0, 0, 0.3)' : '0 1px 2px rgba(255, 255, 255, 0.5)',
                                                  zIndex: 1,
                                                  position: 'relative',
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'visible'
                                                }}
                                              >
                                                No Industry Data
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                                ))}
                              </Grid>
                            )}
                          </Box>
                        );
                      });
                    })()}
                    
                    {/* Card View Summary */}
                    <Box sx={{ 
                      mt: 3, 
                      p: 2, 
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'transparent',
                      borderRadius: 1,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {formatNumber(filteredAnalyticsData.length)} leads grouped by country and company
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Table View */}
                {viewMode === 'table' && (
                 <TableContainer 
                   className={`dashboard-table-container no-expand-table-container ${theme.palette.mode === 'dark' ? 'dark-mode' : ''}`}
                   sx={{ 
                     maxHeight: '70vh',
                     width: '100%',
                     maxWidth: '100%', 
                     overflowX: 'auto',
                     overflowY: 'auto',
                     border: `1px solid ${theme.palette.divider}`,
                     borderRadius: 1
                   }}>
                   <Table className="no-expand-table" stickyHeader size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
                     <TableHead>
                       <TableRow>
                          {allColumns.map((column: string, index: number) => (
                           <ResizableTableCell 
                             key={index}
                             columnId={column}
                             isPopupTable={false}
                             sx={{ 
                               fontWeight: 'bold',
                               backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                               whiteSpace: 'nowrap',
                               position: 'sticky',
                               top: 0,
                               zIndex: 10,
                               borderRight: `1px solid ${theme.palette.divider}`,
                             }}
                           >
                             {cleanColumnHeader(column)}
                           </ResizableTableCell>
                         ))}
                       </TableRow>
                     </TableHead>
                     <TableBody>
                        {filteredAnalyticsData.map((row: any, rowIndex: number) => (
                         <TableRow 
                           key={rowIndex}
                           sx={{ 
                             '&:nth-of-type(odd)': { 
                               backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' 
                             },
                             '&:hover': {
                               backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' 
                             }
                           }}
                         >
                          {allColumns.map((column: string, colIndex: number) => (
                           <TableCell 
                             key={colIndex}
                             sx={{ 
                               fontSize: '0.875rem',
                               borderRight: `1px solid ${theme.palette.divider}`,
                               padding: '8px 12px',
                               whiteSpace: 'normal',
                               wordWrap: 'break-word',
                             }}
                             title={String(row[column] || '')}
                           >
                             {cleanDisplayValueLocal(row[column], column, row)}
                           </TableCell>
                         ))}
                         </TableRow>
                       ))}
                     </TableBody>
                   </Table>
                 </TableContainer>
                )}
                 
                 {/* Data Summary */}
                {viewMode === 'table' && (
                 <Box sx={{ 
                   p: 2, 
                   backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                   borderTop: `1px solid ${theme.palette.divider}`
                 }}>
                   <Typography variant="body2" color="text.secondary" align="center">
                      Showing {formatNumber(filteredAnalyticsData.length)} leads{selectedCountryFilter !== 'All Countries' && ` from ${selectedCountryFilter}`}
                   </Typography>
                 </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Container>
      
      {/* Company Details Popup */}
      <Dialog
        open={popupOpen}
        onClose={handleClosePopup}
        maxWidth="lg"
        fullWidth
        disableScrollLock={true}
        className="company-popup-modal"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0, mr: 2 }}>
              <Typography className="company-popup-title">
                {selectedCompany?.name || 'Company Details'}
              </Typography>
              <Typography className="company-popup-subtitle">
                {cleanDisplayValue(selectedCompany?.country, 'Country')} • {selectedCompany?.totalLeads} leads
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 0 auto', minWidth: 'fit-content' }}>
              <Box className="company-popup-show-columns-control" sx={{ minWidth: 200 }}>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Show Columns</InputLabel>
                  <Select
                    multiple
                    value={companyLeadsFilter}
                    onChange={(e) => setCompanyLeadsFilter(e.target.value as string[])}
                    label="Show Columns"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={value} 
                            size="small"
                            sx={{
                              backgroundColor: '#1B4672',
                              color: '#ffffff',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: '#0F3A5F',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {selectedCompany?.allData && getUsefulColumns(selectedCompany.allData).map((column) => (
                      <MenuItem key={column} value={column}>
                        <Checkbox checked={companyLeadsFilter.indexOf(column) > -1} />
                        <ListItemText primary={column} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <Button
                className="company-popup-filter-button"
                onClick={() => setCompanyLeadsFilter([])}
                sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}
              >
                Clear Filter
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box className="company-popup-content">
            {/* Leads Table */}
            {selectedCompany?.allData && (
              <Box sx={{ 
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'hidden'
              }}>
                <TableContainer 
                  component={Paper} 
                  className={`client-table-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                  sx={{ borderRadius: 2, boxShadow: 2, backgroundColor: theme.palette.mode === 'dark' ? '#2e2e30' : '#ffffff' }}
                >
                  <Table className={`client-table ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                    <TableHead>
                      <TableRow 
                        className={`client-table-header ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                        sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}
                      >
                        <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>First Name</TableCell>
                        <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Last Name</TableCell>
                        <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Phone</TableCell>
                        <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Work Email</TableCell>
                        <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Job Title</TableCell>
                        <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Company Name</TableCell>
                        <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', textAlign: 'center' }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Filter the data based on selected columns
                        const filteredData = companyLeadsFilter.length > 0 
                          ? selectedCompany.allData.map((lead: any) => {
                              const filteredLead: any = {};
                              // Always include essential columns
                              const essentialColumns = ['First Name', 'Last Name', 'Phone', 'Work Email', 'Job Title', 'Company Name'];
                              essentialColumns.forEach(col => {
                                if (lead.hasOwnProperty(col)) {
                                  filteredLead[col] = lead[col];
                                }
                              });
                              // Add selected additional columns
                              companyLeadsFilter.forEach(col => {
                                if (lead.hasOwnProperty(col) && !essentialColumns.includes(col)) {
                                  filteredLead[col] = lead[col];
                                }
                              });
                              return filteredLead;
                            })
                          : selectedCompany.allData;

                        return filteredData.map((lead: any, index: number) => (
                          <React.Fragment key={index}>
                          {/* Main Row - Essential Columns */}
                          <TableRow 
                            className={`client-table-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                            sx={{ 
                              '&:hover': { 
                                backgroundColor: 'transparent'
                              },
                              cursor: 'pointer'
                            }}
                            onClick={() => handleExpandLeadRow(index)}
                          >
                            <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                              <Typography 
                                variant="body1" 
                                className={`table-company-name ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                sx={{ fontWeight: 'medium' }}
                              >
                                {cleanDisplayValueLocal(lead['First Name'], 'First Name')}
                              </Typography>
                            </TableCell>
                            <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                              <Typography 
                                variant="body1" 
                                className={`table-company-name ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                sx={{ fontWeight: 'medium' }}
                              >
                                {cleanDisplayValueLocal(lead['Last Name'], 'Last Name')}
                              </Typography>
                            </TableCell>
                            <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                              <Typography 
                                variant="body2" 
                                className={`table-users-count ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                sx={{ fontWeight: 'medium' }}
                              >
                                {cleanDisplayValueLocal(lead['Phone'], 'Phone')}
                              </Typography>
                            </TableCell>
                            <TableCell className={`client-table-cell company-popup-email-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography 
                                  variant="body2" 
                                  className={`table-users-count ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                  sx={{ fontWeight: 'medium' }}
                                >
                                  {cleanDisplayValueLocal(lead['Work Email'], 'Work Email')}
                                </Typography>
                                {lead._emailValidationStatus === 'valid' && (
                                  <Tooltip title="Verified Email" arrow>
                                    <CheckCircleIcon 
                                      sx={{ 
                                        fontSize: 16, 
                                        color: '#4CAF50',
                                        flexShrink: 0
                                      }} 
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                              <Typography 
                                variant="body2" 
                                className={`table-users-count ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                sx={{ fontWeight: 'medium' }}
                              >
                                {cleanDisplayValueLocal(lead['Job Title'], 'Job Title')}
                              </Typography>
                            </TableCell>
                            <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                              <Typography 
                                variant="body2" 
                                className={`table-users-count ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                sx={{ fontWeight: 'medium' }}
                              >
                                {cleanDisplayValueLocal(lead['Company Name'], 'Company Name')}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={expandedLeadRow === index ? 'Collapse row' : 'Expand row'}>
                                <IconButton
                                  size="small"
                                  className={`expand-collapse-button ${expandedLeadRow === index ? 'expanded' : 'collapsed'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExpandLeadRow(index);
                                  }}
                                  sx={{
                                    backgroundColor: expandedLeadRow === index ? '#FEE2E2' : '#CCF4CD',
                                    color: expandedLeadRow === index ? '#DC2626' : '#00C805',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                      backgroundColor: expandedLeadRow === index ? '#FECACA' : '#B8E6B8',
                                      transform: 'scale(1.05)',
                                    }
                                  }}
                                >
                                  {expandedLeadRow === index ? (
                                    <RemoveIcon sx={{ fontSize: 16, transition: 'transform 0.3s ease-in-out' }} />
                                  ) : (
                                    <AddIcon sx={{ fontSize: 16, transition: 'transform 0.3s ease-in-out' }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded Row - Additional Details */}
                          <TableRow>
                            <TableCell colSpan={7} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                              <Collapse in={expandedLeadRow === index} timeout="auto" unmountOnExit>
                                <Box sx={{ 
                                  padding: 2, 
                                  backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255,255,255,0.02)' 
                                    : 'rgba(0,0,0,0.02)',
                                  borderTop: `1px solid ${theme.palette.divider}`
                                }}>
                                  <Typography 
                                    variant="h6" 
                                    className={`company-details-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                    sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}
                                  >
                                    Additional Details
                                  </Typography>
                                  
                                  {/* Data Viewer - All Columns */}
                                  <Box>
                                    <Box sx={{ 
                                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                                      borderRadius: 2,
                                      border: `1px solid ${theme.palette.divider}`,
                                      overflow: 'hidden'
                                    }}>
                                      <TableContainer>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <ResizableDetailTableCell 
                                                columnId="field"
                                                className={`dashboard-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                sx={{ fontWeight: 'bold' }}
                                              >
                                                Field
                                              </ResizableDetailTableCell>
                                              <ResizableDetailTableCell 
                                                columnId="value"
                                                className={`dashboard-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                sx={{ fontWeight: 'bold' }}
                                              >
                                                Value
                                              </ResizableDetailTableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(() => {
                                              const essentialColumns = ['First Name', 'Last Name', 'Phone', 'Work Email', 'Job Title', 'Company Name'];
                                              const systemFields = ['_uploadVersion', '_uploadId', 'version'];
                                              const allColumns = Object.keys(lead).filter(key => key && key.trim() !== '');
                                              const additionalColumns = allColumns.filter(col => 
                                                !essentialColumns.includes(col) && 
                                                !systemFields.includes(col) &&
                                                !col.startsWith('_')
                                              );
                                              
                                              return additionalColumns.map((column) => (
                                                <TableRow key={column}>
                                                  <ResizableDetailTableCell 
                                                    columnId="field"
                                                    className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                    sx={{ fontWeight: 'medium' }}
                                                  >
                                                    {cleanColumnHeader(column)}
                                                  </ResizableDetailTableCell>
                                                  <ResizableDetailTableCell 
                                                    columnId="value"
                                                    className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                    sx={{ fontWeight: 'medium' }}
                                                  >
                                                    {cleanDisplayValueLocal(lead[column], column, lead) || 'N/A'}
                                                  </ResizableDetailTableCell>
                                                </TableRow>
                                              ));
                                            })()}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </Box>
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                          </React.Fragment>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleClosePopup} 
            className="company-popup-close-button"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Normalize keywords for cross-sheet comparisons (case/spacing/punctuation insensitive)
const normalizeKeywordForMatch = (value: string): string => {
  return value
    .toLowerCase()
    .trim()
    .replace(/[.,!?;:'"()]/g, '')
    .replace(/[-_/]/g, ' ')
    .replace(/\s+/g, ' ');
};

export default ClientDashboard;